import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../db/database';
import RichTextEditor from '../components/editor/RichTextEditor';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib';
import { useUI } from '../context/UIContext';
import { pushWorkspaceBackup } from '../services/cloudBackup';
import {
  getBusinessProfile,
  reserveDocumentNumber,
  generateProfessionalPdf,
  getApprovalStatus,
  getApprovalOverview,
  restoreApprovedVersion,
  recoverUnchangedApprovedVersion,
  downloadApprovedSnapshot,
  sendForApproval,
  resendApproval,
  revokeApproval,
  emailApprovedPdfToClient,
} from '../services/documentService';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { buildProposalExportHtml, isSectionEmpty } from '../utils/proposalExport';
import OperationProgressModal from '../components/proposal-editor/OperationProgressModal';
import { useResizableSplitPane } from '../hooks/useResizableSplitPane';
import ProposalPreview from '../components/proposal-editor/ProposalPreview';
import ProposalFinancials from '../components/proposal-editor/ProposalFinancials';
import ApprovalStatusCard from '../components/proposal-editor/ApprovalStatusCard';
import SendForApprovalModal from '../components/proposal-editor/SendForApprovalModal';

const PDF_INFO_MESSAGES = [
  'Structuring proposal sections…',
  'Formatting investment and milestone tables…',
  'Applying business branding…',
  'Preparing global terms and appendix…',
  'Paginating A4 document pages…',
  'Completing final document checks…',
];

const isPublishedStatus = (status) => status === 'Published' || status === 'Sent';
const canonicalStatus = (status) => (isPublishedStatus(status) ? 'Published' : status || 'Draft');

const incrementMinorVersion = (version) => {
  if (!version) return '1.0';
  const parts = version.split('.');
  const major = parseInt(parts[0]) || 1;
  const minor = parseInt(parts[1]) || 0;
  return `${major}.${minor + 1}`;
};

const normaliseApprovalVersion = (value) => {
  const str = String(value || '').trim();
  if (!str) return '0.0';
  const clean = str.replace(/^v/i, '');
  const parts = clean.split('.');
  const major = parseInt(parts[0], 10) || 0;
  const minor = parts.length > 1 ? parseInt(parts[1], 10) || 0 : 0;
  return `${major}.${minor}`;
};

const isExactApprovalMatch = (approval, docNumber, docVersion) => {
  if (!approval) return false;
  const aDocNum = approval.documentNumber || approval.proposalDocumentNumber || '';
  const aVer = approval.documentVersion || approval.version || '';
  return aDocNum === docNumber && normaliseApprovalVersion(aVer) === normaliseApprovalVersion(docVersion);
};

const isValidApproved = (approval) => {
  if (!approval) return false;
  if (approval.status !== 'APPROVED') return false;
  if (!approval.decisionAt || isNaN(new Date(approval.decisionAt).getTime())) return false;
  return true;
};

function validateSowName(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'SOW name is required.';
  if (trimmed.length < 3 || trimmed.length > 120) return 'Name must be 3 to 120 characters.';
  if (!/^[A-Za-z0-]/.test(trimmed)) return 'Use letters, numbers, spaces, hyphens or underscores only.';
  if (!/[A-Za-z0-9]/.test(trimmed)) return 'Name must contain at least one letter or number.';
  return '';
}

function sanitizeFilename(title) {
  const sanitized = (title || 'Untitled')
    .replace(/[^A-Za-z0-9 _-]/g, '-')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim();
  return sanitized || 'Untitled';
}

const base64PdfToUint8Array = (base64) => {
  if (!base64) return new Uint8Array(0);
  const cleanBase64 = base64
    .replace(/^data:application\/pdf;base64,/i, '')
    .replace(/\s+/g, '');
  const binaryString = atob(cleanBase64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToBase64 = (bytes) => {
  let binary = '';
  const len = bytes.byteLength;
  const chunkSize = 0x8000;
  for (let i = 0; i < len; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
};

const formatApprovalDatePdf = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  } catch {
    return '—';
  }
};

// ── DOM-First PDF Pagination Helpers ──
const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;
const PAGE_PADDING = 32;
const OVERFLOW_TOLERANCE = 4;

const nextFrame = () => new Promise((r) => requestAnimationFrame(r));

const waitForAssets = async (el) => {
  const imgs = el.querySelectorAll('img');
  if (imgs.length > 0) {
    await new Promise((resolve) => {
      let remaining = imgs.length;
      const onSettle = () => {
        remaining--;
        if (remaining <= 0) resolve();
      };
      imgs.forEach((img) => {
        if (img.complete) onSettle();
        else {
          img.addEventListener('load', onSettle, { once: true });
          img.addEventListener('error', onSettle, { once: true });
        }
      });
    });
  }
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) {}
  }
  await nextFrame();
  await nextFrame();
};

const createPremiumPdfPaginationSandbox = () => {
  const stale = document.getElementById('premium-pdf-pagination-sandbox');
  if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
  const sandbox = document.createElement('div');
  sandbox.id = 'premium-pdf-pagination-sandbox';
  sandbox.style.position = 'fixed';
  sandbox.style.left = '-20000px';
  sandbox.style.top = '0';
  sandbox.style.width = `${PAGE_WIDTH}px`;
  sandbox.style.background = '#ffffff';
  sandbox.style.pointerEvents = 'none';
  sandbox.style.zIndex = '-9999';
  document.body.appendChild(sandbox);
  return sandbox;
};

const createPremiumPdfDomPage = (sandbox) => {
  const pageElement = document.createElement('div');
  pageElement.className = 'premium-pdf-page';
  const pageContentElement = document.createElement('div');
  pageContentElement.className = 'premium-pdf-page-content';
  const pageDocumentElement = document.createElement('div');
  pageDocumentElement.className = 'export-doc pdf-page-doc';
  pageContentElement.appendChild(pageDocumentElement);
  pageElement.appendChild(pageContentElement);
  sandbox.appendChild(pageElement);
  return { pageElement, pageContentElement, pageDocumentElement };
};

const getPdfPageOverflowAmount = (pageContentElement) => {
  const scrollOverflow = pageContentElement.scrollHeight - pageContentElement.clientHeight;
  let descendantOverflow = 0;
  try {
    const contentRect = pageContentElement.getBoundingClientRect();
    const descendants = pageContentElement.querySelectorAll('*');
    for (const el of descendants) {
      const rect = el.getBoundingClientRect();
      const elBottom = rect.bottom - contentRect.top;
      if (elBottom > descendantOverflow) descendantOverflow = elBottom;
    }
    descendantOverflow = descendantOverflow - contentRect.height;
  } catch (e) {}
  return Math.max(0, Math.max(scrollOverflow, descendantOverflow));
};

const doesPdfPageOverflow = (pageContentElement) => {
  return getPdfPageOverflowAmount(pageContentElement) > OVERFLOW_TOLERANCE;
};

const isPageContentMeaningful = (pageDocumentElement) => {
  const text = (pageDocumentElement.textContent || '').trim();
  if (text.length > 0) return true;
  const imgs = pageDocumentElement.querySelectorAll('img');
  for (const img of imgs) {
    if (img.naturalWidth > 1 && img.naturalHeight > 1) return true;
  }
  const rows = pageDocumentElement.querySelectorAll('table tr');
  if (rows.length > 0) return true;
  const hrs = pageDocumentElement.querySelectorAll('hr');
  if (hrs.length > 0) return true;
  const svgs = pageDocumentElement.querySelectorAll('svg');
  if (svgs.length > 0) return true;
  return false;
};

const finalizePremiumPdfPage = async (pageState, pages, sandbox) => {
  await nextFrame();
  const { pageElement, pageContentElement, pageDocumentElement } = pageState;
  if (!pageElement.parentNode) return;
  if (pageElement.dataset.pdfFinalized === 'true') {
    if (!pages.includes(pageState)) pages.push(pageState);
    return;
  }
  if (!isPageContentMeaningful(pageDocumentElement)) {
    if (pageElement.parentNode) sandbox.removeChild(pageElement);
    return;
  }
  if (doesPdfPageOverflow(pageContentElement)) {
    if (pageElement.parentNode) sandbox.removeChild(pageElement);
    throw new Error('page overflow before finalisation');
  }
  pageElement.dataset.pdfFinalized = 'true';
  pages.push(pageState);
};

const getDirectPdfFlowRoot = (block) => {
  if (!block || !block.children) return null;
  return Array.from(block.children).find((child) => child.hasAttribute('data-pdf-flow-root')) || null;
};

const applyPdfFragmentIdentity = (element, sourceId, fragmentIndex) => {
  if (sourceId) element.setAttribute('data-pdf-source-id', sourceId);
  if (fragmentIndex !== null && fragmentIndex !== undefined) {
    element.setAttribute('data-pdf-fragment-index', String(fragmentIndex));
  }
};

const createFlowContinuationState = (originalBlock, sandbox, sourceId, fragmentIndex) => {
  const pageState = createPremiumPdfDomPage(sandbox);
  const sectionClone = originalBlock.cloneNode(true);
  const flowRootElement = getDirectPdfFlowRoot(sectionClone);
  if (!flowRootElement) {
    if (pageState.pageElement.parentNode) sandbox.removeChild(pageState.pageElement);
    throw new Error('Section does not contain a direct data-pdf-flow-root');
  }
  flowRootElement.innerHTML = '';
  applyPdfFragmentIdentity(sectionClone, sourceId, fragmentIndex);
  pageState.pageDocumentElement.appendChild(sectionClone);
  return { currentPage: pageState, pageDocumentElement: pageState.pageDocumentElement, sectionClone, flowRootElement };
};

const splitPdfListAcrossPages = async (listElement, flowState, pages, sandbox, createFlowContinuation, topLevelSourceId, getFragmentIndex) => {
  let { currentPage, pageDocumentElement, sectionClone, flowRootElement } = flowState;
  const tagName = listElement.tagName;
  const isOrdered = tagName === 'OL';
  const listItems = Array.from(listElement.children);
  const listClass = listElement.className;
  const listStyle = listElement.getAttribute('style') || '';
  let currentList = listElement.cloneNode(false);
  currentList.className = listClass;
  if (listStyle) currentList.setAttribute('style', listStyle);
  if (topLevelSourceId && getFragmentIndex) applyPdfFragmentIdentity(currentList, topLevelSourceId, getFragmentIndex());
  let orderedStart = 1;
  flowRootElement.appendChild(currentList);
  for (let idx = 0; idx < listItems.length; idx++) {
    const itemClone = listItems[idx].cloneNode(true);
    currentList.appendChild(itemClone);
    await nextFrame();
    if (!doesPdfPageOverflow(currentPage.pageContentElement)) {
      if (isOrdered) orderedStart++;
      continue;
    }
    currentList.removeChild(itemClone);
    await finalizePremiumPdfPage(currentPage, pages, sandbox);
    const cont = createFlowContinuation();
    currentPage = cont.currentPage;
    pageDocumentElement = cont.pageDocumentElement;
    sectionClone = cont.sectionClone;
    flowRootElement = cont.flowRootElement;
    if (!currentPage.pageElement.contains(flowRootElement) || !flowRootElement.isConnected) {
      throw new Error('Invalid PDF continuation state');
    }
    currentList = listElement.cloneNode(false);
    currentList.className = listClass;
    if (listStyle) currentList.setAttribute('style', listStyle);
    if (isOrdered) currentList.setAttribute('start', String(orderedStart));
    if (topLevelSourceId && getFragmentIndex) applyPdfFragmentIdentity(currentList, topLevelSourceId, getFragmentIndex());
    flowRootElement.appendChild(currentList);
    currentList.appendChild(itemClone);
    await nextFrame();
    if (isOrdered) orderedStart++;
    if (doesPdfPageOverflow(currentPage.pageContentElement)) {
      currentList.removeChild(itemClone);
      const subPages = await splitOversizedPdfTextNode(itemClone, sandbox, topLevelSourceId, getFragmentIndex);
      for (const sp of subPages) { await finalizePremiumPdfPage(sp, pages, sandbox); }
      const cont2 = createFlowContinuation();
      currentPage = cont2.currentPage;
      pageDocumentElement = cont2.pageDocumentElement;
      sectionClone = cont2.sectionClone;
      flowRootElement = cont2.flowRootElement;
      currentList = listElement.cloneNode(false);
      currentList.className = listClass;
      if (listStyle) currentList.setAttribute('style', listStyle);
      if (isOrdered) currentList.setAttribute('start', String(orderedStart));
      if (topLevelSourceId && getFragmentIndex) applyPdfFragmentIdentity(currentList, topLevelSourceId, getFragmentIndex());
      flowRootElement.appendChild(currentList);
    }
  }
  return { currentPage, pageDocumentElement, sectionClone, flowRootElement };
};

const splitPdfTableAcrossPages = async (tableElement, flowState, pages, sandbox, createFlowContinuation, topLevelSourceId, getFragmentIndex) => {
  let { currentPage, pageDocumentElement, sectionClone, flowRootElement } = flowState;
  const tableClass = tableElement.className;
  const tableStyle = tableElement.getAttribute('style') || '';
  const thead = tableElement.querySelector('thead');
  const tbody = tableElement.querySelector('tbody');
  const colgroup = tableElement.querySelector('colgroup');
  const dataRows = tbody ? Array.from(tbody.children) : Array.from(tableElement.children).filter((r) => r.tagName === 'TR');
  const buildTableWithHeader = () => {
    const tbl = tableElement.cloneNode(false);
    tbl.className = tableClass;
    if (tableStyle) tbl.setAttribute('style', tableStyle);
    if (topLevelSourceId && getFragmentIndex) applyPdfFragmentIdentity(tbl, topLevelSourceId, getFragmentIndex());
    if (colgroup) tbl.appendChild(colgroup.cloneNode(true));
    if (thead) tbl.appendChild(thead.cloneNode(true));
    const body = document.createElement('tbody');
    tbl.appendChild(body);
    return { tbl, body };
  };
  let { tbl: currentTable, body: currentBody } = buildTableWithHeader();
  flowRootElement.appendChild(currentTable);
  for (const row of dataRows) {
    const rowClone = row.cloneNode(true);
    currentBody.appendChild(rowClone);
    await nextFrame();
    if (!doesPdfPageOverflow(currentPage.pageContentElement)) continue;
    currentBody.removeChild(rowClone);
    await finalizePremiumPdfPage(currentPage, pages, sandbox);
    const cont = createFlowContinuation();
    currentPage = cont.currentPage;
    pageDocumentElement = cont.pageDocumentElement;
    sectionClone = cont.sectionClone;
    flowRootElement = cont.flowRootElement;
    if (!currentPage.pageElement.contains(flowRootElement) || !flowRootElement.isConnected) {
      throw new Error('Invalid PDF continuation state');
    }
    const built = buildTableWithHeader();
    currentTable = built.tbl;
    currentBody = built.body;
    flowRootElement.appendChild(currentTable);
    currentBody.appendChild(rowClone);
    await nextFrame();
  }
  return { currentPage, pageDocumentElement, sectionClone, flowRootElement };
};

const splitOversizedPdfTextNode = async (element, sandbox, sourceId, getFragmentIndex) => {
  const pages = [];
  let pageState = createPremiumPdfDomPage(sandbox);
  let containerEl = pageState.pageDocumentElement;
  const tag = element.tagName;
  const style = element.getAttribute('style') || '';
  const className = element.className;
  const text = element.textContent || '';
  const words = text.split(/(\s+)/);
  let fittingPart = document.createElement(tag);
  if (className) fittingPart.className = className;
  if (style) fittingPart.setAttribute('style', style);
  if (sourceId && getFragmentIndex) applyPdfFragmentIdentity(fittingPart, sourceId, getFragmentIndex());
  containerEl.appendChild(fittingPart);
  let currentText = '';
  for (let i = 0; i < words.length; i++) {
    const testText = currentText + words[i];
    fittingPart.textContent = testText;
    await nextFrame();
    if (!doesPdfPageOverflow(pageState.pageContentElement)) { currentText = testText; continue; }
    fittingPart.textContent = currentText;
    await finalizePremiumPdfPage(pageState, pages, sandbox);
    pageState = createPremiumPdfDomPage(sandbox);
    containerEl = pageState.pageDocumentElement;
    fittingPart = document.createElement(tag);
    if (className) fittingPart.className = className;
    if (style) fittingPart.setAttribute('style', style);
    if (sourceId && getFragmentIndex) applyPdfFragmentIdentity(fittingPart, sourceId, getFragmentIndex());
    containerEl.appendChild(fittingPart);
    currentText = words[i].trim() ? words[i] : '';
    fittingPart.textContent = currentText;
    await nextFrame();
  }
  if (fittingPart.textContent.trim()) { await finalizePremiumPdfPage(pageState, pages, sandbox); }
  else { if (pageState.pageElement.parentNode) sandbox.removeChild(pageState.pageElement); }
  return pages;
};

const placeSplittableBlock = async (block, sandbox) => {
  const pages = [];
  let pageState = createPremiumPdfDomPage(sandbox);
  let pageDocumentElement = pageState.pageDocumentElement;
  const sourceId = block.getAttribute('data-pdf-source-id');
  let currentFragmentIndex = 0;
  const getFragmentIndex = () => currentFragmentIndex++;
  const flowRoot = getDirectPdfFlowRoot(block);
  const table = block.matches('[data-pdf-table]') ? block : block.querySelector('[data-pdf-table]');
  const list = block.querySelector('ul,ol');
  if (flowRoot) {
    const flowChildren = Array.from(flowRoot.children);
    const createContinuation = () => createFlowContinuationState(block, sandbox, sourceId, getFragmentIndex());
    let state = { currentPage: pageState, pageDocumentElement: pageState.pageDocumentElement, sectionClone: null, flowRootElement: null };
    const sectionClone = block.cloneNode(true);
    const cloneFlow = getDirectPdfFlowRoot(sectionClone);
    if (cloneFlow) cloneFlow.innerHTML = '';
    applyPdfFragmentIdentity(sectionClone, sourceId, getFragmentIndex());
    pageState.pageDocumentElement.appendChild(sectionClone);
    state.sectionClone = sectionClone;
    state.flowRootElement = cloneFlow;
    for (const child of flowChildren) {
      if (!state.currentPage.pageElement.contains(state.flowRootElement) || !state.flowRootElement.isConnected) throw new Error('Invalid PDF continuation state');
      if (state.currentPage.pageElement.dataset.pdfFinalized === 'true') throw new Error('Invalid PDF continuation state');
      const childClone = child.cloneNode(true);
      state.flowRootElement.appendChild(childClone);
      await nextFrame();
      if (!doesPdfPageOverflow(state.currentPage.pageContentElement)) continue;
      state.flowRootElement.removeChild(childClone);
      if (childClone.tagName === 'UL' || childClone.tagName === 'OL') {
        state = await splitPdfListAcrossPages(childClone, state, pages, sandbox, createContinuation, sourceId, getFragmentIndex);
        continue;
      }
      if (childClone.querySelector && childClone.querySelector('[data-pdf-table]')) {
        const innerTable = childClone.querySelector('[data-pdf-table]');
        state = await splitPdfTableAcrossPages(innerTable, state, pages, sandbox, createContinuation, sourceId, getFragmentIndex);
        continue;
      }
      const testPage = createPremiumPdfDomPage(sandbox);
      testPage.pageDocumentElement.appendChild(childClone.cloneNode(true));
      await nextFrame();
      const childFitsAlone = !doesPdfPageOverflow(testPage.pageContentElement);
      if (testPage.pageElement.parentNode) sandbox.removeChild(testPage.pageElement);
      if (childFitsAlone) {
        await finalizePremiumPdfPage(state.currentPage, pages, sandbox);
        state = createContinuation();
        state.flowRootElement.appendChild(childClone);
        await nextFrame();
        if (doesPdfPageOverflow(state.currentPage.pageContentElement)) {
          state.flowRootElement.removeChild(childClone);
          const subPages = await splitOversizedPdfTextNode(childClone, sandbox, sourceId, getFragmentIndex);
          for (const sp of subPages) { await finalizePremiumPdfPage(sp, pages, sandbox); }
          state = createContinuation();
        }
      } else {
        const subPages = await splitOversizedPdfTextNode(childClone, sandbox, sourceId, getFragmentIndex);
        for (const sp of subPages) { await finalizePremiumPdfPage(sp, pages, sandbox); }
        state = createContinuation();
      }
    }
    if (state.sectionClone && state.sectionClone.parentNode) { await finalizePremiumPdfPage(state.currentPage, pages, sandbox); }
    else { if (state.currentPage.pageElement.parentNode) sandbox.removeChild(state.currentPage.pageElement); }
    return pages;
  }
  if (table) {
    let state = { currentPage: pageState, pageDocumentElement: pageState.pageDocumentElement, sectionClone: null, flowRootElement: pageState.pageDocumentElement };
    state = await splitPdfTableAcrossPages(table, state, pages, sandbox, () => {
      const ps = createPremiumPdfDomPage(sandbox);
      return { currentPage: ps, pageDocumentElement: ps.pageDocumentElement, sectionClone: null, flowRootElement: ps.pageDocumentElement };
    }, sourceId, getFragmentIndex);
    await finalizePremiumPdfPage(state.currentPage, pages, sandbox);
    return pages;
  }
  if (list) {
    let state = { currentPage: pageState, pageDocumentElement: pageState.pageDocumentElement, sectionClone: null, flowRootElement: pageState.pageDocumentElement };
    state = await splitPdfListAcrossPages(list, state, pages, sandbox, () => {
      const ps = createPremiumPdfDomPage(sandbox);
      return { currentPage: ps, pageDocumentElement: ps.pageDocumentElement, sectionClone: null, flowRootElement: ps.pageDocumentElement };
    }, sourceId, getFragmentIndex);
    await finalizePremiumPdfPage(state.currentPage, pages, sandbox);
    return pages;
  }
  if (block.children.length > 1) {
    let compositeClone = block.cloneNode(false);
    applyPdfFragmentIdentity(compositeClone, sourceId, getFragmentIndex());
    pageDocumentElement.appendChild(compositeClone);
    for (const child of Array.from(block.children)) {
      if (pageState.pageElement.dataset.pdfFinalized === 'true') throw new Error('Invalid PDF continuation state');
      const childClone = child.cloneNode(true);
      compositeClone.appendChild(childClone);
      await nextFrame();
      if (!doesPdfPageOverflow(pageState.pageContentElement)) continue;
      compositeClone.removeChild(childClone);
      await finalizePremiumPdfPage(pageState, pages, sandbox);
      pageState = createPremiumPdfDomPage(sandbox);
      pageDocumentElement = pageState.pageDocumentElement;
      compositeClone = block.cloneNode(false);
      applyPdfFragmentIdentity(compositeClone, sourceId, getFragmentIndex());
      pageDocumentElement.appendChild(compositeClone);
      compositeClone.appendChild(childClone);
      await nextFrame();
      if (doesPdfPageOverflow(pageState.pageContentElement)) {
        compositeClone.removeChild(childClone);
        const subPages = await splitOversizedPdfTextNode(childClone, sandbox, sourceId, getFragmentIndex);
        for (const sp of subPages) { await finalizePremiumPdfPage(sp, pages, sandbox); }
        pageState = createPremiumPdfDomPage(sandbox);
        pageDocumentElement = pageState.pageDocumentElement;
        compositeClone = block.cloneNode(false);
        applyPdfFragmentIdentity(compositeClone, sourceId, getFragmentIndex());
        pageDocumentElement.appendChild(compositeClone);
      }
    }
    await finalizePremiumPdfPage(pageState, pages, sandbox);
    return pages;
  }
  const subPages = await splitOversizedPdfTextNode(block, sandbox, sourceId, getFragmentIndex);
  for (const sp of subPages) { await finalizePremiumPdfPage(sp, pages, sandbox); }
  return pages;
};

const paginatePremiumDocumentDom = async (sourceExportDoc, sandbox) => {
  const pages = [];
  let pageState = createPremiumPdfDomPage(sandbox);
  let currentDoc = pageState.pageDocumentElement;
  const sourceChildren = Array.from(sourceExportDoc.children).filter((el) => el.tagName !== 'STYLE');
  sourceChildren.forEach((child, idx) => { child.setAttribute('data-pdf-source-id', `src-${idx}`); });
  for (let i = 0; i < sourceChildren.length; i++) {
    const sourceChild = sourceChildren[i];
    const clone = sourceChild.cloneNode(true);
    const sourceId = sourceChild.getAttribute('data-pdf-source-id');
    clone.removeAttribute('data-pdf-source-id');
    if (sourceId) clone.setAttribute('data-pdf-source-id', sourceId);
    if (pageState.pageElement.dataset.pdfFinalized === 'true') throw new Error('Invalid PDF continuation state');
    currentDoc.appendChild(clone);
    await nextFrame();
    if (!doesPdfPageOverflow(pageState.pageContentElement)) continue;
    currentDoc.removeChild(clone);
    if (currentDoc.children.length === 0) {
      const splitPages = await placeSplittableBlock(clone, sandbox);
      for (const sp of splitPages) pages.push(sp);
      pageState = createPremiumPdfDomPage(sandbox);
      currentDoc = pageState.pageDocumentElement;
      continue;
    }
    const hasKeepWithNext = clone.hasAttribute('data-pdf-keep-with-next') || clone.querySelector('[data-pdf-keep-with-next]');
    if (hasKeepWithNext && i + 1 < sourceChildren.length) {
      const nextClone = sourceChildren[i + 1].cloneNode(true);
      const nextSourceId = sourceChildren[i + 1].getAttribute('data-pdf-source-id');
      nextClone.removeAttribute('data-pdf-source-id');
      if (nextSourceId) nextClone.setAttribute('data-pdf-source-id', nextSourceId);
      await finalizePremiumPdfPage(pageState, pages, sandbox);
      pageState = createPremiumPdfDomPage(sandbox);
      currentDoc = pageState.pageDocumentElement;
      currentDoc.appendChild(clone);
      currentDoc.appendChild(nextClone);
      await nextFrame();
      if (!doesPdfPageOverflow(pageState.pageContentElement)) { i++; continue; }
      currentDoc.removeChild(nextClone);
      await nextFrame();
      if (!doesPdfPageOverflow(pageState.pageContentElement)) { continue; }
      currentDoc.removeChild(clone);
      const splitPages = await placeSplittableBlock(clone, sandbox);
      for (const sp of splitPages) pages.push(sp);
      pageState = createPremiumPdfDomPage(sandbox);
      currentDoc = pageState.pageDocumentElement;
      continue;
    }
    if (clone.hasAttribute('data-pdf-keep-together')) {
      const testPage = createPremiumPdfDomPage(sandbox);
      testPage.pageDocumentElement.appendChild(clone.cloneNode(true));
      await nextFrame();
      if (!doesPdfPageOverflow(testPage.pageContentElement)) {
        if (testPage.pageElement.parentNode) sandbox.removeChild(testPage.pageElement);
        await finalizePremiumPdfPage(pageState, pages, sandbox);
        pageState = createPremiumPdfDomPage(sandbox);
        currentDoc = pageState.pageDocumentElement;
        currentDoc.appendChild(clone);
        continue;
      }
      if (testPage.pageElement.parentNode) sandbox.removeChild(testPage.pageElement);
    }
    await finalizePremiumPdfPage(pageState, pages, sandbox);
    pageState = createPremiumPdfDomPage(sandbox);
    currentDoc = pageState.pageDocumentElement;
    currentDoc.appendChild(clone);
    await nextFrame();
    if (doesPdfPageOverflow(pageState.pageContentElement)) {
      currentDoc.removeChild(clone);
      const splitPages = await placeSplittableBlock(clone, sandbox);
      if (pageState.pageElement.parentNode) sandbox.removeChild(pageState.pageElement);
      for (const sp of splitPages) pages.push(sp);
      pageState = createPremiumPdfDomPage(sandbox);
      currentDoc = pageState.pageDocumentElement;
    }
  }
  if (currentDoc.children.length > 0) { await finalizePremiumPdfPage(pageState, pages, sandbox); }
  else { if (pageState.pageElement.parentNode) sandbox.removeChild(pageState.pageElement); }
  return pages;
};

const validatePaginatedDocumentCompleteness = (sourceExportDoc, pages) => {
  const sourceBlocks = Array.from(sourceExportDoc.children).filter((el) => el.tagName !== 'STYLE');
  const sourceIds = new Set();
  sourceBlocks.forEach((b) => { const id = b.getAttribute('data-pdf-source-id'); if (id) sourceIds.add(id); });
  const foundIds = new Map();
  const fragmentIndexes = new Map();
  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const page = pages[pageIdx];
    if (!page.pageDocumentElement.children.length) throw new Error(`Page ${pageIdx + 1} is empty`);
    const overflow = getPdfPageOverflowAmount(page.pageContentElement);
    if (overflow > OVERFLOW_TOLERANCE) {
      let lastText = '';
      try {
        const allText = page.pageContentElement.querySelectorAll('p, li, td, h1, h2, h3, h4, h5, h6, span');
        for (let j = allText.length - 1; j >= 0; j--) { const t = (allText[j].textContent || '').trim(); if (t.length > 0) { lastText = t; break; } }
      } catch (e) {}
      const snippet = lastText.substring(0, 80);
      console.error(`Premium PDF page ${pageIdx + 1} overflowed by ${Math.round(overflow)}px near:\n"${snippet}…"`);
      throw new Error(`Page ${pageIdx + 1} overflow detected`);
    }
    const pageSourceEls = page.pageDocumentElement.querySelectorAll('[data-pdf-source-id]');
    pageSourceEls.forEach((el) => {
      const sid = el.getAttribute('data-pdf-source-id');
      if (!sid) return;
      if (!foundIds.has(sid)) foundIds.set(sid, 0);
      foundIds.set(sid, foundIds.get(sid) + 1);
      const fragIdx = el.getAttribute('data-pdf-fragment-index');
      if (fragIdx !== null) { if (!fragmentIndexes.has(sid)) fragmentIndexes.set(sid, new Set()); fragmentIndexes.get(sid).add(parseInt(fragIdx)); }
    });
  }
  for (const sid of sourceIds) {
    if (!foundIds.has(sid)) throw new Error(`Source block ${sid} is missing from paginated output`);
    const count = foundIds.get(sid);
    if (count > 1) {
      const frags = fragmentIndexes.get(sid);
      if (!frags) throw new Error(`Source block ${sid} appears ${count} times without fragment indexes`);
      if (frags.size !== count) throw new Error(`Source block ${sid} has duplicate fragment indexes`);
      for (let i = 0; i < count; i++) { if (!frags.has(i)) throw new Error(`Source block ${sid} is missing fragment index ${i}`); }
    }
  }
  if (pages.length === 0) throw new Error('No pages were generated');
  const lastPage = pages[pages.length - 1];
  if (!isPageContentMeaningful(lastPage.pageDocumentElement)) throw new Error('Trailing blank page detected');
  return true;
};

export default function ProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, confirm, promptText } = useUI();
  const prefersReducedMotion = useReducedMotion();
  const isNewDocument = id === 'new';
  const { leftWidth, setLeftWidth, containerRef, isResizing, startResizing } = useResizableSplitPane();
  const [proposal, setProposal] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [removeSpacing, setRemoveSpacing] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [isPublishedView, setIsPublishedView] = useState(false);
  const [publishedPromptOpen, setPublishedPromptOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [approvalData, setApprovalData] = useState(null);
  const [approvalOverview, setApprovalOverview] = useState(null);
  const [restoreApprovalInProgress, setRestoreApprovalInProgress] = useState(false);
  const [recoverUnchangedInProgress, setRecoverUnchangedInProgress] = useState(false);
  const [historicalPdfDownloadInProgress, setHistoricalPdfDownloadInProgress] = useState(false);
  const [approvedPdfEmailInProgress, setApprovedPdfEmailInProgress] = useState(false);
  const [approvedPdfEmailStage, setApprovedPdfEmailStage] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [sendApprovalModalOpen, setSendApprovalModalOpen] = useState(false);
  const [sendApprovalLoading, setSendApprovalLoading] = useState(false);
  const [sendApprovalError, setSendApprovalError] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [operationModalOpen, setOperationModalOpen] = useState(false);
  const [operationStatus, setOperationStatus] = useState('active');
  const [operationType, setOperationType] = useState(null);
  const [operationStage, setOperationStage] = useState('');
  const [operationDescription, setOperationDescription] = useState('');
  const [operationProgress, setOperationProgress] = useState(0);
  const [operationMessage, setOperationMessage] = useState('');
  const [operationElapsedSeconds, setOperationElapsedSeconds] = useState(0);
  const [operationError, setOperationError] = useState(null);
  const [operationResult, setOperationResult] = useState(null);
  const [operationLocalSaved, setOperationLocalSaved] = useState(false);
  const [premiumPdfInProgress, setPremiumPdfInProgress] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(null);
  const previewRef = useRef(null);
  const documentNumberRequestRef = useRef(null);
  const operationInProgressRef = useRef(null);
  const publishInProgressRef = useRef(false);
  const isMountedRef = useRef(true);
  const publishedPromptShownRef = useRef(false);
  const moreMenuRef = useRef(null);
  const approvalFetchedRef = useRef(null);
  const sendApprovalInProgressRef = useRef(false);
  const saveDraftInProgressRef = useRef(false);
  const profileLoadedRef = useRef(null);
  const operationElapsedTimerRef = useRef(null);
  const operationInfoMsgIntervalRef = useRef(null);
  const operationEstProgressIntervalRef = useRef(null);
  const operationSuccessTimeoutRef = useRef(null);
  const namingDialogShownRef = useRef(false);
  const premiumPdfInProgressRef = useRef(false);
  const approvedPdfEmailInProgressRef = useRef(false);
  const lastApprovedRequestIdRef = useRef(null);
  const lastApprovedDocumentNumberRef = useRef(null);
  const lastApprovedVersionRef = useRef(null);
  const lastApprovedDecisionAtRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; clearOperationTimers(); };
  }, []);

  const clearOperationTimers = useCallback(() => {
    clearInterval(operationElapsedTimerRef.current);
    clearInterval(operationInfoMsgIntervalRef.current);
    clearInterval(operationEstProgressIntervalRef.current);
    clearTimeout(operationSuccessTimeoutRef.current);
    operationElapsedTimerRef.current = null;
    operationInfoMsgIntervalRef.current = null;
    operationEstProgressIntervalRef.current = null;
    operationSuccessTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (event) => { if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) setMoreMenuOpen(false); };
    const handleKeyDown = (event) => { if (event.key === 'Escape') setMoreMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('mousedown', handleClickOutside); window.removeEventListener('keydown', handleKeyDown); };
  }, [moreMenuOpen]);

  useEffect(() => {
    if (!proposal || publishedPromptShownRef.current) return;
    if (isPublishedStatus(proposal.status)) { publishedPromptShownRef.current = true; setPublishedPromptOpen(true); }
  }, [proposal?.id, proposal?.status]);

  useEffect(() => {
    const loadData = async () => {
      setIsPublishedView(false);
      publishedPromptShownRef.current = false;
      setPublishedPromptOpen(false);
      setMoreMenuOpen(false);
      approvalFetchedRef.current = null;
      setApprovalData(null);
      setApprovalOverview(null);
      setBusinessProfile(null);
      profileLoadedRef.current = null;
      lastApprovedRequestIdRef.current = null;
      lastApprovedDocumentNumberRef.current = null;
      lastApprovedVersionRef.current = null;
      lastApprovedDecisionAtRef.current = null;
      if (isNewDocument) {
        const settingsData = await db.settings.get('global');
        setSettings(settingsData);
        const navTitle = location.state?.initialSowTitle;
        const trimmedNavTitle = navTitle ? navTitle.trim() : '';
        const hasValidNavTitle = trimmedNavTitle && !validateSowName(trimmedNavTitle);
        if (hasValidNavTitle) {
          const defaultProposal = {
            id: 'new', title: trimmedNavTitle, clientName: '', clientMobile: '', status: 'Draft', createdAt: Date.now(), updatedAt: Date.now(),
            sections: { inScope: '<h3>Project Objectives</h3><p>Detail the primary goals...</p>', deliverables: '<ul><li>Phase 1 Deliverable</li><li>Phase 2 Deliverable</li></ul>', slas: '<p>Standard SLA response time is 24 hours.</p>', supportTimings: '<p>Support is available Monday to Friday, 9 AM to 5 PM AEST.</p>', exclusions: '<p>Items not specifically listed as in-scope are excluded, including...</p>', revisionLimits: '<p>Two (2) rounds of consolidated feedback are included per phase.</p>', clientDependencies: '<p>Client must provide all brand assets and copy within 5 business days of kickoff.</p>', definitionOfDone: '<p>Deliverable is considered complete when client provides written sign-off via email.</p>', changeControl: '<p>Requests outside this SOW will be evaluated and quoted as a separate Change Order.</p>', nextSteps: '<h3>NEXT STEPS</h3><p>To proceed:</p><ol><li>Confirm acceptance of this proposal</li><li>Deposit invoice will be issued upon approval</li><li>Project will be scheduled accordingly</li></ol><p>Project work will commence only after the deposit has been received.</p>', pointOfContact: '' },
            financials: { items: [{ id: crypto.randomUUID(), description: 'Initial Phase', amount: 0 }], taxEnabled: true },
            paymentMilestones: [{ id: crypto.randomUUID(), label: 'Deposit', percent: 50 }, { id: crypto.randomUUID(), label: 'Mid Point', percent: 25 }, { id: crypto.randomUUID(), label: 'On Completion', percent: 25 }],
            commercialNotes: '', terms: settingsData?.defaultTnc || '<p>Payment terms: 14 days from milestone delivery.</p>',
          };
          lastSavedRef.current = JSON.stringify(defaultProposal);
          setProposal(defaultProposal);
          return;
        }
        if (!namingDialogShownRef.current) {
          namingDialogShownRef.current = true;
          const name = await promptText({ title: 'Name your Statement of Work', message: 'Enter a clear internal and client-facing name for this SOW.', label: 'SOW name', placeholder: 'ASG-Operations_Job-Costing', helperText: 'Letters, numbers, spaces, hyphens and underscores only.', confirmText: 'Create SOW', cancelText: 'Cancel', maxLength: 120, validate: validateSowName });
          if (!name) { navigate('/proposals', { replace: true }); return; }
          const defaultProposal = {
            id: 'new', title: name, clientName: '', clientMobile: '', status: 'Draft', createdAt: Date.now(), updatedAt: Date.now(),
            sections: { inScope: '<h3>Project Objectives</h3><p>Detail the primary goals...</p>', deliverables: '<ul><li>Phase 1 Deliverable</li><li>Phase 2 Deliverable</li></ul>', slas: '<p>Standard SLA response time is 24 hours.</p>', supportTimings: '<p>Support is available Monday to Friday, 9 AM to 5 PM AEST.</p>', exclusions: '<p>Items not specifically listed as in-scope are excluded, including...</p>', revisionLimits: '<p>Two (2) rounds of consolidated feedback are included per phase.</p>', clientDependencies: '<p>Client must provide all brand assets and copy within 5 business days of kickoff.</p>', definitionOfDone: '<p>Deliverable is considered complete when client provides written sign-off via email.</p>', changeControl: '<p>Requests outside this SOW will be evaluated and quoted as a separate Change Order.</p>', nextSteps: '<h3>NEXT STEPS</h3><p>To proceed:</p><ol><li>Confirm acceptance of this proposal</li><li>Deposit invoice will be issued upon approval</li><li>Project will be scheduled accordingly</li></ol><p>Project work will commence only after the deposit has been received.</p>', pointOfContact: '' },
            financials: { items: [{ id: crypto.randomUUID(), description: 'Initial Phase', amount: 0 }], taxEnabled: true },
            paymentMilestones: [{ id: crypto.randomUUID(), label: 'Deposit', percent: 50 }, { id: crypto.randomUUID(), label: 'Mid Point', percent: 25 }, { id: crypto.randomUUID(), label: 'On Completion', percent: 25 }],
            commercialNotes: '', terms: settingsData?.defaultTnc || '<p>Payment terms: 14 days from milestone delivery.</p>',
          };
          lastSavedRef.current = JSON.stringify(defaultProposal);
          setProposal(defaultProposal);
        }
        return;
      }
      const prop = await db.proposals.get(id);
      if (prop) {
        if (!prop.sections) prop.sections = {};
        if (prop.commercialNotes === undefined) prop.commercialNotes = '';
        if (prop.emailAddress === undefined) prop.emailAddress = '';
        if (prop.clientMobile === undefined) prop.clientMobile = '';
        if (!prop.paymentMilestones) { prop.paymentMilestones = [{ id: crypto.randomUUID(), label: 'Deposit', percent: 50 }, { id: crypto.randomUUID(), label: 'Mid Point', percent: 25 }, { id: crypto.randomUUID(), label: 'On Completion', percent: 25 }]; }
        if (prop.sections.nextSteps === undefined) { prop.sections.nextSteps = '<h3>NEXT STEPS</h3><p>To proceed:</p><ol><li>Confirm acceptance of this proposal</li><li>Deposit invoice will be issued upon approval</li><li>Project will be scheduled accordingly</li></ol><p>Project work will commence only after the deposit has been received.</p>'; }
        lastSavedRef.current = JSON.stringify(prop);
        setProposal(prop);
        setApprovalData(prop.approval && prop.approval.status ? { ...prop.approval } : null);
        if (isPublishedStatus(prop.status)) { publishedPromptShownRef.current = true; setPublishedPromptOpen(true); }
      } else { navigate('/proposals'); }
      const sets = await db.settings.get('global');
      setSettings(sets);
    };
    loadData();
  }, [id, navigate, isNewDocument, location.state, promptText]);

  useEffect(() => {
    if (!settings?.googleBackupConnected || !settings?.googleBackupUrl) { setBusinessProfile(null); profileLoadedRef.current = null; return; }
    const settingsKey = settings.googleBackupUrl;
    if (profileLoadedRef.current === settingsKey) return;
    profileLoadedRef.current = settingsKey;
    let cancelled = false;
    const loadProfile = async () => {
      try { const profile = await getBusinessProfile(settings, true); if (!cancelled && isMountedRef.current) setBusinessProfile(profile); }
      catch (err) { console.warn('Could not load business profile for preview:', err); if (!cancelled && isMountedRef.current) setBusinessProfile(null); }
    };
    loadProfile();
    return () => { cancelled = true; };
  }, [settings?.googleBackupConnected, settings?.googleBackupUrl]);

  useEffect(() => {
    if (!proposal || isPublishedView) return;
    if (isNewDocument) return;
    const currentDataString = JSON.stringify(proposal);
    if (currentDataString === lastSavedRef.current) return;
    setIsSaving(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const updatedProp = { ...proposal, updatedAt: Date.now() };
      await db.proposals.put(updatedProp);
      lastSavedRef.current = JSON.stringify(updatedProp);
      setIsSaving(false);
    }, 1500);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [proposal, isPublishedView, isNewDocument]);

  useEffect(() => {
    if (!proposal || !settings) return;
    if (proposal.documentNumber) return;
    if (!settings.googleBackupConnected || !settings.googleBackupUrl) return;
    if (isNewDocument) return;
    if (documentNumberRequestRef.current === proposal.id) return;
    documentNumberRequestRef.current = proposal.id;
    let isCancelled = false;
    const fetchNumber = async () => {
      try {
        const latestSettings = await db.settings.get('global');
        if (!latestSettings?.googleBackupConnected || isCancelled) return;
        const res = await reserveDocumentNumber(latestSettings, proposal.id, latestSettings.documentPrefix || 'SOW');
        if (isCancelled || !res.documentNumber) return;
        const now = Date.now();
        await db.proposals.update(proposal.id, { documentNumber: res.documentNumber, documentNumberAssignedAt: res.documentNumberAssignedAt || new Date().toISOString(), updatedAt: now });
        setProposal((prev) => { if (!prev || prev.id !== proposal.id) return prev; return { ...prev, documentNumber: res.documentNumber, documentNumberAssignedAt: res.documentNumberAssignedAt || new Date().toISOString(), updatedAt: now }; });
      } catch (err) { console.warn('Failed to reserve document number:', err); documentNumberRequestRef.current = null; }
    };
    fetchNumber();
    return () => { isCancelled = true; };
  }, [proposal?.id, proposal?.documentNumber, settings?.googleBackupConnected, settings?.googleBackupUrl, isNewDocument]);

  const persistApprovalSnapshot = useCallback(async (approval) => {
    if (!proposal || isNewDocument || !proposal.id || proposal.id === 'new') return;
    const snapshot = approval?.status ? {
      status: approval.status, decisionAt: approval.decisionAt || null, sentAt: approval.sentAt || null, requestId: approval.requestId || null,
      recipientName: approval.recipientName || null, recipientEmail: approval.recipientEmail || null, clientName: approval.clientName || null, clientEmail: approval.clientEmail || null,
      version: approval.version || null, documentVersion: approval.documentVersion || null, documentNumber: approval.documentNumber || null, proposalDocumentNumber: approval.proposalDocumentNumber || null,
      receiptUrl: approval.receiptUrl || null, receiptDriveUrl: approval.receiptDriveUrl || null,
      emailSendStatus: approval.emailSendStatus || '', emailitEmailId: approval.emailitEmailId || '', emailitProviderStatus: approval.emailitProviderStatus || '',
      emailSendDurationMs: approval.emailSendDurationMs || 0, deliveryAttemptCount: approval.deliveryAttemptCount || 0,
      sendErrorCode: approval.sendErrorCode || '', sendErrorMessage: approval.sendErrorMessage || '', sendErrorAt: approval.sendErrorAt || '',
      approvedPdfFileId: approval.approvedPdfFileId || '', approvedPdfEmailStatus: approval.approvedPdfEmailStatus || '',
      approvedPdfEmailSentAt: approval.approvedPdfEmailSentAt || '', approvedPdfEmailProvider: approval.approvedPdfEmailProvider || '',
    } : { status: 'NOT_SENT', decisionAt: null };
    try {
      await db.proposals.update(proposal.id, { approval: snapshot });
      setProposal((prev) => { if (!prev || prev.id !== proposal.id) return prev; return { ...prev, approval: snapshot }; });
    } catch (err) { console.warn('Failed to persist approval snapshot:', err); }
  }, [proposal, isNewDocument]);

  const resolveCanonicalApproval = useCallback((overview) => {
    if (!overview) return { status: 'NOT_SENT' };
    if (overview.exactApproved && isValidApproved(overview.exactApproved)) return overview.exactApproved;
    if (overview.latestExactRequest) return overview.latestExactRequest;
    if (overview.requestedRequest && isExactApprovalMatch(overview.requestedRequest, proposal?.documentNumber, proposal?.version)) return overview.requestedRequest;
    return { status: 'NOT_SENT' };
  }, [proposal?.documentNumber, proposal?.version]);

  const resolveApprovedExportEvidence = useCallback(async () => {
    if (!proposal || isNewDocument || !proposal.id || proposal.id === 'new') return null;
    const currentVersion = proposal.version || '1.0';
    const currentDocNumber = proposal.documentNumber || null;
    const validateExact = (approval) => {
      if (!approval) return null;
      const statusNorm = String(approval.status || '').trim().toUpperCase();
      if (statusNorm !== 'APPROVED') return null;
      const decisionAt = approval.decisionAt;
      if (!decisionAt || isNaN(new Date(decisionAt).getTime())) return null;
      const approvalVersionRaw = approval.documentVersion || approval.version || null;
      const approvalVersionNorm = normaliseApprovalVersion(approvalVersionRaw);
      const currentVersionNorm = normaliseApprovalVersion(currentVersion);
      if (!approvalVersionRaw || approvalVersionNorm !== currentVersionNorm) return null;
      const approvalDocNumber = approval.documentNumber || approval.proposalDocumentNumber || null;
      if (currentDocNumber && approvalDocNumber && String(approvalDocNumber) !== String(currentDocNumber)) return null;
      if (!approval.requestId) return null;
      return { status: 'APPROVED', decisionAt, sentAt: approval.sentAt || null, recipientName: approval.recipientName || approval.clientName || '', recipientEmail: approval.recipientEmail || approval.clientEmail || '', requestId: approval.requestId || null, documentNumber: approval.documentNumber || approval.proposalDocumentNumber || null, documentVersion: approvalVersionRaw, method: 'SCOPESHIELD_SECURE_APPROVAL' };
    };
    let exactApproved = approvalOverview?.exactApproved || null;
    if (!exactApproved && settings?.googleBackupConnected && settings?.googleBackupUrl && proposal.id && proposal.id !== 'new' && proposal.documentNumber && proposal.version) {
      try {
        const overview = await getApprovalOverview(settings, { requestId: proposal.approvalRequestId || approvalData?.requestId || proposal.approval?.requestId || '', proposalId: proposal.id, documentNumber: proposal.documentNumber, documentVersion: proposal.version });
        if (isMountedRef.current) { setApprovalOverview(overview); exactApproved = overview.exactApproved; }
      } catch (err) { console.warn('Approval overview refresh failed for export:', err); }
    }
    if (exactApproved) return validateExact(exactApproved);
    if (approvalData && isValidApproved(approvalData) && isExactApprovalMatch(approvalData, currentDocNumber, currentVersion)) return validateExact(approvalData);
    const localSnapshot = proposal.approval;
    if (localSnapshot && isValidApproved(localSnapshot) && isExactApprovalMatch(localSnapshot, currentDocNumber, currentVersion)) return validateExact(localSnapshot);
    return null;
  }, [proposal, isNewDocument, approvalData, settings, approvalOverview]);

  const buildApprovedPdfPackage = async (approvalEvidence) => {
    const result = await downloadApprovedSnapshot(settings, { proposalId: proposal.id, documentNumber: approvalEvidence.documentNumber, approvedRequestId: approvalEvidence.requestId, approvedDocumentVersion: approvalEvidence.documentVersion });
    if (!result.pdfBase64 || !result.fileName) throw new Error('Server did not return a valid PDF snapshot.');
    if (result.documentNumber !== approvalEvidence.documentNumber) throw new Error('Snapshot document number mismatch.');
    if (normaliseApprovalVersion(result.documentVersion) !== normaliseApprovalVersion(approvalEvidence.documentVersion)) throw new Error('Snapshot document version mismatch.');
    const sourceBytes = base64PdfToUint8Array(result.pdfBase64);
    if (sourceBytes.length === 0) throw new Error('Decoded PDF bytes are empty.');
    result.pdfBase64 = null;
    const pdfDoc = await PDFDocument.load(sourceBytes);
    const sourcePageCount = pdfDoc.getPageCount();
    if (sourcePageCount === 0) throw new Error('Source PDF contains no pages.');
    const page = pdfDoc.insertPage(0, [595.28, 841.89]);
    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
    const darkPurple = rgb(47 / 255, 23 / 255, 71 / 255);
    const gold = rgb(199 / 255, 154 / 255, 59 / 255);
    const green = rgb(23 / 255, 132 / 255, 75 / 255);
    const darkGreen = rgb(15 / 255, 80 / 255, 45 / 255);
    const ink = rgb(23 / 255, 32 / 255, 51 / 255);
    const muted = rgb(102 / 255, 112 / 255, 133 / 255);
    page.drawText('ScopeShield', { x: 50, y: 790, size: 14, font: helveticaBold, color: darkPurple });
    page.drawText('APPROVED COPY', { x: 50, y: 765, size: 24, font: helveticaBold, color: gold });
    page.drawText('Approval Confirmation', { x: 50, y: 710, size: 18, font: helveticaBold, color: darkPurple });
    page.drawText('Approved electronically through the ScopeShield secure approval workflow.', { x: 50, y: 690, size: 11, font: helvetica, color: green });
    let currentY = 640;
    const drawRow = (label, value) => { if (!value) return; page.drawText(label, { x: 50, y: currentY, size: 9, font: helveticaBold, color: muted }); page.drawText(value, { x: 200, y: currentY, size: 11, font: helvetica, color: ink }); currentY -= 24; };
    drawRow('SOW number', approvalEvidence.documentNumber);
    drawRow('Approved version', approvalEvidence.documentVersion);
    drawRow('Approved on', formatApprovalDatePdf(approvalEvidence.decisionAt));
    drawRow('Approved by', approvalEvidence.recipientName);
    drawRow('Approval request sent to', approvalEvidence.recipientEmail);
    drawRow('Approval request sent on', formatApprovalDatePdf(approvalEvidence.sentAt));
    drawRow('Approval reference', approvalEvidence.requestId);
    currentY -= 20;
    page.drawText('The following pages are the exact PDF originally issued to the client', { x: 50, y: currentY, size: 10, font: helveticaOblique, color: muted });
    currentY -= 14;
    page.drawText('for review and preserved in the ScopeShield approval record.', { x: 50, y: currentY, size: 10, font: helveticaOblique, color: muted });
    const finalPageCount = pdfDoc.getPageCount();
    if (finalPageCount !== sourcePageCount + 1) throw new Error('Page count mismatch after inserting certificate.');
    const watermarkText = 'APPROVED COPY';
    const edgeText = `APPROVED COPY | ${approvalEvidence.documentNumber} | Version ${approvalEvidence.documentVersion}`;
    for (let i = 1; i < finalPageCount; i++) {
      const p = pdfDoc.getPage(i);
      const { width, height } = p.getSize();
      if (width === 0 || height === 0) throw new Error(`Original page ${i} has zero dimensions.`);
      const wmSize = width > 600 ? 42 : 34;
      const wmWidth = helveticaBold.widthOfTextAtSize(watermarkText, wmSize);
      p.drawText(watermarkText, { x: (width - wmWidth) / 2, y: height / 2 - 20, size: wmSize, font: helveticaBold, color: green, opacity: 0.04, rotate: degrees(30) });
      const edgeSize = 6;
      p.drawText(edgeText, { x: width - 12, y: height - 40, size: edgeSize, font: helveticaBold, color: darkGreen, rotate: degrees(270) });
    }
    const outputBytes = await pdfDoc.save({ useObjectStreams: true, addDefaultPage: false });
    if (outputBytes.length === 0) throw new Error('Output PDF is empty.');
    return { outputBytes, fileName: result.fileName, sourcePageCount, finalPageCount };
  };

  const generateApprovedPdfFromOriginalSnapshot = async (approvalEvidence) => {
    let objectUrl = null;
    try {
      const { outputBytes } = await buildApprovedPdfPackage(approvalEvidence);
      const blob = new Blob([outputBytes], { type: 'application/pdf' });
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const safeFilename = sanitizeFilename(proposal.title);
      const sowNumber = proposal.documentNumber || '';
      a.download = `${safeFilename}${sowNumber ? `_${sowNumber}` : ''}_Approved_SOW.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      showToast('Approved PDF downloaded successfully from the preserved original approval copy.');
    } catch (err) {
      console.error('Approved PDF generation failed:', err);
      showToast(err.message ? `The Approved PDF could not be created from the preserved approval copy. ${err.message}` : 'The Approved PDF could not be created from the preserved approval copy. Download the Original Approved Copy and Approval Receipt instead.', 'error');
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      premiumPdfInProgressRef.current = false;
      if (isMountedRef.current) setPremiumPdfInProgress(false);
      setMoreMenuOpen(false);
    }
  };

  const handleEmailApprovedPdfToClient = async () => {
    if (approvedPdfEmailInProgressRef.current) return;
    approvedPdfEmailInProgressRef.current = true;
    setApprovedPdfEmailInProgress(true);
    let base64Data = null;
    let outputBytesRef = null;
    try {
      setApprovedPdfEmailStage('Preparing the approved document');
      const approvalEvidence = await resolveApprovedExportEvidence();
      if (!approvalEvidence) throw new Error('Approval evidence could not be resolved for this exact document version.');
      if (!approvalEvidence.recipientEmail) throw new Error('No recipient email address is recorded for this approval.');

      setApprovedPdfEmailStage('Securing the approval certificate');
      const pkg = await buildApprovedPdfPackage(approvalEvidence);
      outputBytesRef = pkg.outputBytes;

      if (!outputBytesRef || outputBytesRef.byteLength === 0) {
        throw new Error('The Approved PDF package is empty.');
      }
      const MAX_PDF_BYTES = 12 * 1024 * 1024;
      if (outputBytesRef.byteLength > MAX_PDF_BYTES) {
        throw new Error('The Approved PDF exceeds the 12 MB email limit. Download it and send it manually.');
      }

      setApprovedPdfEmailStage('Uploading the approved PDF');
      const safeFilename = sanitizeFilename(proposal.title);
      const sowNumber = proposal.documentNumber || '';
      const finalFileName = `${safeFilename}${sowNumber ? `_${sowNumber}` : ''}_Approved_SOW.pdf`;
      base64Data = uint8ArrayToBase64(outputBytesRef);

      setApprovedPdfEmailStage('Sending the polished client email');
      const result = await emailApprovedPdfToClient(settings, {
        proposalId: proposal.id,
        requestId: approvalEvidence.requestId,
        documentNumber: approvalEvidence.documentNumber,
        documentVersion: approvalEvidence.documentVersion,
        fileName: finalFileName,
        pdfBase64: base64Data,
        customMessage: '',
      });

      setApprovedPdfEmailStage('Confirming completion');
      if (isMountedRef.current) {
        setApprovalData(result);
        persistApprovalSnapshot(result);

        // Merge returned approval into existing overview without a new round trip
        setApprovalOverview((prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          if (updated.exactApproved) {
            updated.exactApproved = { ...updated.exactApproved, ...result };
          } else {
            updated.exactApproved = result;
          }
          if (updated.latestApproved) {
            updated.latestApproved = { ...updated.latestApproved, ...result };
          }
          if (updated.latestExactRequest && updated.latestExactRequest.requestId === result.requestId) {
            updated.latestExactRequest = { ...updated.latestExactRequest, ...result };
          }
          return updated;
        });
      }

      const recipientEmail = result.recipientEmail || approvalEvidence.recipientEmail;
      showToast(`Approved PDF emailed to ${recipientEmail}`);
    } catch (err) {
      console.error('Email Approved PDF failed:', err);
      showToast(`Failed to email Approved PDF: ${err.message}`, 'error');
    } finally {
      base64Data = null;
      outputBytesRef = null;
      approvedPdfEmailInProgressRef.current = false;
      if (isMountedRef.current) {
        setApprovedPdfEmailInProgress(false);
        setApprovedPdfEmailStage('');
      }
    }
  };

  useEffect(() => {
    if (!proposal || !settings || isNewDocument) return;
    if (!settings.googleBackupConnected || !settings.googleBackupUrl) return;
    if (!proposal.documentNumber || !proposal.version) return;
    if (approvalFetchedRef.current === proposal.id) return;
    approvalFetchedRef.current = proposal.id;
    const fetchApproval = async () => {
      setApprovalLoading(true);
      try {
        const overview = await getApprovalOverview(settings, { requestId: proposal.approvalRequestId || approvalData?.requestId || proposal.approval?.requestId || '', proposalId: proposal.id, documentNumber: proposal.documentNumber, documentVersion: proposal.version });
        if (isMountedRef.current) {
          setApprovalOverview(overview);
          const canonical = resolveCanonicalApproval(overview);
          setApprovalData(canonical);
          persistApprovalSnapshot(canonical);
          if (overview.exactApproved && isValidApproved(overview.exactApproved)) {
            lastApprovedRequestIdRef.current = overview.exactApproved.requestId || null;
            lastApprovedDocumentNumberRef.current = overview.exactApproved.documentNumber || overview.exactApproved.proposalDocumentNumber || null;
            lastApprovedVersionRef.current = overview.exactApproved.documentVersion || overview.exactApproved.version || null;
            lastApprovedDecisionAtRef.current = overview.exactApproved.decisionAt || null;
          }
        }
      } catch (err) {
        if (isMountedRef.current) showToast('Approval status could not be refreshed. Showing last known status.', 'error');
      } finally {
        if (isMountedRef.current) setApprovalLoading(false);
      }
    };
    fetchApproval();
  }, [proposal?.id, proposal?.documentNumber, proposal?.version, settings?.googleBackupConnected, settings?.googleBackupUrl, isNewDocument, persistApprovalSnapshot, showToast]);

  const handleChange = (field, value) => setProposal((prev) => ({ ...prev, [field]: value }));
  const handleSectionChange = (section, value) => setProposal((prev) => ({ ...prev, sections: { ...prev.sections, [section]: value } }));
  const handleFinancialChange = (index, field, value) => { setProposal((prev) => ({ ...prev, financials: { ...prev.financials, items: prev.financials.items.map((item, i) => i === index ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : item) } })); };
  const addFinancialItem = () => { setProposal((prev) => ({ ...prev, financials: { ...prev.financials, items: [...prev.financials.items, { id: crypto.randomUUID(), description: '', amount: 0 }] } })); };
  const applyPreset = (preset) => { const newItems = preset.items.map((item) => ({ id: crypto.randomUUID(), ...item })); setProposal((prev) => ({ ...prev, financials: { ...prev.financials, items: newItems } })); setShowPresets(false); showToast(`Applied ${preset.name} preset`); };
  const removeFinancialItem = (index) => { setProposal((prev) => ({ ...prev, financials: { ...prev.financials, items: prev.financials.items.filter((_, i) => i !== index) } })); };
  const handleMilestoneChange = (index, field, value) => { setProposal((prev) => ({ ...prev, paymentMilestones: prev.paymentMilestones.map((m, i) => i === index ? { ...m, [field]: field === 'percent' ? parseFloat(value) || 0 : value } : m) })); };
  const addMilestone = () => { setProposal((prev) => ({ ...prev, paymentMilestones: [...(prev.paymentMilestones || []), { id: crypto.randomUUID(), label: '', percent: 0 }] })); };
  const removeMilestone = (index) => { setProposal((prev) => ({ ...prev, paymentMilestones: prev.paymentMilestones.filter((_, i) => i !== index) })); };
  const handleTaxToggle = (checked) => setProposal((prev) => ({ ...prev, financials: { ...prev.financials, taxEnabled: checked } }));
  const handleCommercialNotesChange = (val) => setProposal((prev) => ({ ...prev, commercialNotes: val }));

  const handleRefreshApproval = async () => {
    if (!proposal || !settings) return;
    setApprovalLoading(true);
    try {
      if (settings.googleBackupConnected && settings.googleBackupUrl && proposal.documentNumber && proposal.version) {
        const overview = await getApprovalOverview(settings, { requestId: proposal.approvalRequestId || approvalData?.requestId || proposal.approval?.requestId || '', proposalId: proposal.id, documentNumber: proposal.documentNumber, documentVersion: proposal.version });
        if (isMountedRef.current) {
          setApprovalOverview(overview);
          const canonical = resolveCanonicalApproval(overview);
          setApprovalData(canonical);
          persistApprovalSnapshot(canonical);
          if (overview.exactApproved && isValidApproved(overview.exactApproved)) {
            lastApprovedRequestIdRef.current = overview.exactApproved.requestId || null;
            lastApprovedDocumentNumberRef.current = overview.exactApproved.documentNumber || overview.exactApproved.proposalDocumentNumber || null;
            lastApprovedVersionRef.current = overview.exactApproved.documentVersion || overview.exactApproved.version || null;
            lastApprovedDecisionAtRef.current = overview.exactApproved.decisionAt || null;
          }
        }
      } else {
        const result = await getApprovalStatus(settings, { requestId: proposal.approvalRequestId || approvalData?.requestId, proposalId: proposal.id });
        if (isMountedRef.current) { const normalized = result || { status: 'NOT_SENT' }; setApprovalData(normalized); persistApprovalSnapshot(normalized); }
      }
    } catch (err) {
      if (isMountedRef.current) showToast('Approval status could not be refreshed. Showing last known status.', 'error');
    } finally {
      if (isMountedRef.current) setApprovalLoading(false);
    }
  };

  const handleResendApproval = async () => {
    if (!proposal || !settings) return;
    const requestId = approvalOverview?.latestExactRequest?.requestId || approvalData?.requestId || proposal.approvalRequestId;
    if (!requestId) return;
    if (sendApprovalInProgressRef.current) return;
    sendApprovalInProgressRef.current = true;
    setApprovalLoading(true);
    try {
      const result = await resendApproval(settings, requestId);
      if (isMountedRef.current) { setApprovalData(result); persistApprovalSnapshot(result); }
      showToast('Approval request resent');
    } catch (err) {
      showToast(`Failed to resend: ${err.message}`, 'error');
      try {
        const refreshResult = await getApprovalStatus(settings, { requestId: proposal.approvalRequestId || approvalData?.requestId, proposalId: proposal.id });
        if (isMountedRef.current && refreshResult) { setApprovalData(refreshResult); persistApprovalSnapshot(refreshResult); }
      } catch (refreshErr) { console.warn('Failed to refresh status after resend error', refreshErr); }
    } finally {
      if (isMountedRef.current) setApprovalLoading(false);
      sendApprovalInProgressRef.current = false;
    }
  };

  const handleRevokeApproval = async () => {
    if (!proposal || !settings) return;
    const requestId = approvalOverview?.latestExactRequest?.requestId || approvalData?.requestId || proposal.approvalRequestId;
    if (!requestId) return;
    const isConfirmed = await confirm({ title: 'Revoke Approval Request?', message: 'This will cancel the pending approval request. The recipient will no longer be able to respond.', type: 'danger', confirmText: 'Revoke', cancelText: 'Cancel' });
    if (!isConfirmed) return;
    setApprovalLoading(true);
    try {
      const result = await revokeApproval(settings, requestId);
      if (isMountedRef.current) { setApprovalData(result); persistApprovalSnapshot(result); }
      showToast('Approval request revoked');
    } catch (err) {
      showToast(`Failed to revoke: ${err.message}`, 'error');
    } finally {
      if (isMountedRef.current) setApprovalLoading(false);
    }
  };

  const handleRestoreApprovedVersion = async () => {
    if (!proposal || !settings) return;
    const exactApproved = approvalOverview?.exactApproved;
    if (!exactApproved || !isValidApproved(exactApproved)) return;
    if (!isExactApprovalMatch(exactApproved, proposal.documentNumber, proposal.version)) return;
    const isConfirmed = await confirm({ title: 'Restore Published + Approved?', message: 'ScopeShield found the original secure approval for this exact SOW number and version. Continue only if the document content has not been changed since it was approved. This restores the original approval reference and timestamp; it does not create a new approval.', confirmText: 'Restore Approved Version', cancelText: 'Cancel' });
    if (!isConfirmed) return;
    setRestoreApprovalInProgress(true);
    try {
      const result = await restoreApprovedVersion(settings, { requestId: exactApproved.requestId, proposalId: proposal.id, documentNumber: proposal.documentNumber, documentVersion: proposal.version, revokeStalePending: true });
      if (!result.approval || result.approval.status !== 'APPROVED') throw new Error('Server did not return a valid APPROVED record for restore.');
      const restoredApproval = result.approval;
      const now = Date.now();
      const updatePayload = {
        status: 'Published', publishedAt: proposal.publishedAt || now, reopenedAt: null, updatedAt: now, approvalRequestId: restoredApproval.requestId,
        approval: { status: restoredApproval.status, decisionAt: restoredApproval.decisionAt, sentAt: restoredApproval.sentAt || null, requestId: restoredApproval.requestId, recipientName: restoredApproval.recipientName || null, recipientEmail: restoredApproval.recipientEmail || null, clientName: restoredApproval.clientName || null, clientEmail: restoredApproval.clientEmail || null, version: restoredApproval.version || restoredApproval.documentVersion || null, documentVersion: restoredApproval.documentVersion || restoredApproval.version || null, documentNumber: restoredApproval.documentNumber || restoredApproval.proposalDocumentNumber || null, proposalDocumentNumber: restoredApproval.proposalDocumentNumber || restoredApproval.documentNumber || null, receiptUrl: restoredApproval.receiptUrl || null, receiptDriveUrl: restoredApproval.receiptDriveUrl || null, emailSendStatus: restoredApproval.emailSendStatus || '', emailitEmailId: restoredApproval.emailitEmailId || '', emailitProviderStatus: restoredApproval.emailitProviderStatus || '', emailSendDurationMs: restoredApproval.emailSendDurationMs || 0, deliveryAttemptCount: restoredApproval.deliveryAttemptCount || 0, sendErrorCode: restoredApproval.sendErrorCode || '', sendErrorMessage: restoredApproval.sendErrorMessage || '', sendErrorAt: restoredApproval.sendErrorAt || '' },
        lastApprovedRequestId: restoredApproval.requestId, lastApprovedDocumentNumber: restoredApproval.documentNumber || restoredApproval.proposalDocumentNumber || null, lastApprovedVersion: restoredApproval.documentVersion || restoredApproval.version || null, lastApprovedDecisionAt: restoredApproval.decisionAt,
      };
      await db.proposals.update(proposal.id, updatePayload);
      if (isMountedRef.current) {
        setProposal((prev) => { if (!prev || prev.id !== proposal.id) return prev; return { ...prev, ...updatePayload }; });
        setApprovalData(restoredApproval);
        setApprovalOverview(result.overview);
        setIsPublishedView(true);
        lastApprovedRequestIdRef.current = restoredApproval.requestId;
        lastApprovedDocumentNumberRef.current = restoredApproval.documentNumber || restoredApproval.proposalDocumentNumber || null;
        lastApprovedVersionRef.current = restoredApproval.documentVersion || restoredApproval.version || null;
        lastApprovedDecisionAtRef.current = restoredApproval.decisionAt;
      }
      showToast('Published and approved status restored.');
      if (result.revokedPendingCount > 0) showToast(`${result.revokedPendingCount} redundant pending approval link(s) revoked.`, 'info');
    } catch (err) {
      showToast(`Failed to restore approved version: ${err.message}`, 'error');
    } finally {
      if (isMountedRef.current) setRestoreApprovalInProgress(false);
    }
  };

  const handleDownloadHistoricalApprovedPdf = async () => {
    if (!proposal || !settings) return;
    if (historicalPdfDownloadInProgress) return;
    const selectedRecord = approvalOverview?.exactApproved || historicalApprovedRecord;
    if (!selectedRecord) return;
    setHistoricalPdfDownloadInProgress(true);
    let objectUrl = null;
    let base64Data = null;
    try {
      const result = await downloadApprovedSnapshot(settings, { proposalId: proposal.id, documentNumber: selectedRecord.documentNumber || selectedRecord.proposalDocumentNumber || proposal.documentNumber, approvedRequestId: selectedRecord.requestId, approvedDocumentVersion: selectedRecord.documentVersion || selectedRecord.version });
      if (!result.pdfBase64 || !result.fileName) throw new Error('Server did not return a valid PDF snapshot.');
      base64Data = result.pdfBase64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      showToast('Original approved PDF downloaded.');
    } catch (err) {
      showToast(`Failed to download original approved PDF: ${err.message}`, 'error');
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      base64Data = null;
      if (isMountedRef.current) setHistoricalPdfDownloadInProgress(false);
    }
  };

  const handleRecoverUnchangedApprovedVersion = async () => {
    if (!proposal || !settings) return;
    if (!historicalApprovedRecord) return;
    if (recoverUnchangedInProgress) return;
    if (isNewDocument || proposal.id === 'new') return;
    if (!settings.googleBackupConnected || !settings.googleBackupUrl) return;
    const approvedVersion = historicalApprovedRecord.documentVersion || historicalApprovedRecord.version || '';
    const currentVersion = proposal.version || '';
    const isConfirmed = await confirm({ title: 'Recover unchanged approved version?', message: `The client approved ${proposal.documentNumber} version ${approvedVersion}. The current version is ${currentVersion}. Use this recovery only when the scope, pricing, deliverables, exclusions, terms and all other document content have remained unchanged. ScopeShield will reset the displayed version to the originally approved version, reconnect the original approval record and revoke redundant pending links. Failed and historical records will remain in the approval history.`, confirmText: 'I confirm the content is unchanged', cancelText: 'Cancel' });
    if (!isConfirmed) return;
    setRecoverUnchangedInProgress(true);
    try {
      const result = await recoverUnchangedApprovedVersion(settings, { proposalId: proposal.id, documentNumber: proposal.documentNumber, currentDocumentVersion: proposal.version, approvedRequestId: historicalApprovedRecord.requestId, approvedDocumentVersion: historicalApprovedRecord.documentVersion || historicalApprovedRecord.version, ownerAttestation: 'UNCHANGED_CONTENT_CONFIRMED', revokeStalePending: true });
      if (!result.approval || result.approval.status !== 'APPROVED') throw new Error('Server did not return a valid APPROVED record for recovery.');
      if (!result.recoveredToVersion) throw new Error('Server did not confirm the recovered version.');
      if (result.originalDecisionPreserved !== true) throw new Error('Server could not preserve the original decision timestamp.');
      const returnedApproval = result.approval;
      const returnedDocNum = returnedApproval.documentNumber || returnedApproval.proposalDocumentNumber || '';
      const returnedVer = returnedApproval.documentVersion || returnedApproval.version || '';
      if (returnedDocNum !== proposal.documentNumber) throw new Error('Returned approval document number does not match current proposal.');
      if (normaliseApprovalVersion(returnedVer) !== normaliseApprovalVersion(result.recoveredToVersion)) throw new Error('Returned approval version does not match recovered version.');
      if (!returnedApproval.decisionAt || isNaN(new Date(returnedApproval.decisionAt).getTime())) throw new Error('Returned approval decision timestamp is invalid.');
      const now = new Date().toISOString();
      const updatePayload = {
        version: result.recoveredToVersion, status: 'Published', publishedAt: proposal.publishedAt || now, reopenedAt: null, updatedAt: now, approvalRequestId: returnedApproval.requestId,
        approval: { status: returnedApproval.status, decisionAt: returnedApproval.decisionAt, sentAt: returnedApproval.sentAt || null, requestId: returnedApproval.requestId, recipientName: returnedApproval.recipientName || null, recipientEmail: returnedApproval.recipientEmail || null, clientName: returnedApproval.clientName || null, clientEmail: returnedApproval.clientEmail || null, version: returnedApproval.version || returnedApproval.documentVersion || null, documentVersion: returnedApproval.documentVersion || returnedApproval.version || null, documentNumber: returnedApproval.documentNumber || returnedApproval.proposalDocumentNumber || null, proposalDocumentNumber: returnedApproval.proposalDocumentNumber || returnedApproval.documentNumber || null, receiptUrl: returnedApproval.receiptUrl || null, receiptDriveUrl: returnedApproval.receiptDriveUrl || null, emailSendStatus: returnedApproval.emailSendStatus || '', emailitEmailId: returnedApproval.emailitEmailId || '', emailitProviderStatus: returnedApproval.emailitProviderStatus || '', emailSendDurationMs: returnedApproval.emailSendDurationMs || 0, deliveryAttemptCount: returnedApproval.deliveryAttemptCount || 0, sendErrorCode: returnedApproval.sendErrorCode || '', sendErrorMessage: returnedApproval.sendErrorMessage || '', sendErrorAt: returnedApproval.sendErrorAt || '' },
        lastApprovedRequestId: returnedApproval.requestId, lastApprovedDocumentNumber: returnedApproval.documentNumber || returnedApproval.proposalDocumentNumber || null, lastApprovedVersion: returnedApproval.documentVersion || returnedApproval.version || null, lastApprovedDecisionAt: returnedApproval.decisionAt,
      };
      await db.proposals.update(proposal.id, updatePayload);
      if (isMountedRef.current) {
        setProposal((prev) => { if (!prev || prev.id !== proposal.id) return prev; return { ...prev, ...updatePayload }; });
        setApprovalData(returnedApproval);
        setApprovalOverview(result.overview);
        await persistApprovalSnapshot(returnedApproval);
        setIsPublishedView(true);
        lastApprovedRequestIdRef.current = returnedApproval.requestId;
        lastApprovedDocumentNumberRef.current = returnedApproval.documentNumber || returnedApproval.proposalDocumentNumber || null;
        lastApprovedVersionRef.current = returnedApproval.documentVersion || returnedApproval.version || null;
        lastApprovedDecisionAtRef.current = returnedApproval.decisionAt;
      }
      showToast('Published and approved status restored.');
      if (result.revokedPendingCount > 0) showToast(`${result.revokedPendingCount} redundant pending approval link(s) were revoked. No audit rows were deleted.`, 'info');
    } catch (err) {
      showToast(`Failed to recover unchanged approved version: ${err.message}`, 'error');
    } finally {
      if (isMountedRef.current) setRecoverUnchangedInProgress(false);
    }
  };

  const handleSendForApproval = () => {
    if (isNewDocument) { showToast('Save the proposal before sending for approval.', 'error'); return; }
    if (!isPublishedStatus(proposal.status)) { showToast('Publish the SOW before sending for approval.', 'error'); return; }
    setMoreMenuOpen(false);
    if (isApprovalApproved) { showToast('This exact SOW number and version has already been approved. Create and publish a new revision before requesting another approval.', 'error'); return; }
    if (isApprovalPending) { showToast('An approval request is already pending. Use Resend in the approval status card.', 'error'); return; }
    setSendApprovalError(null);
    setSendApprovalModalOpen(true);
  };

  const handleSubmitApproval = async ({ recipientName, recipientEmail, recipientMobile, customMessage }) => {
    if (sendApprovalInProgressRef.current) return;
    sendApprovalInProgressRef.current = true;
    setSendApprovalLoading(true);
    setSendApprovalError(null);
    try {
      const result = await sendForApproval(settings, { proposal, recipientName, recipientEmail, recipientMobile, customMessage });
      if (isMountedRef.current) {
        setApprovalData(result);
        persistApprovalSnapshot(result);
        try {
          await db.proposals.update(proposal.id, { clientMobile: recipientMobile });
          setProposal((prev) => { if (!prev || prev.id !== proposal.id) return prev; return { ...prev, clientMobile: recipientMobile }; });
        } catch (err) { console.warn('Failed to persist clientMobile:', err); }
        setSendApprovalModalOpen(false);
        showToast('Approval request sent successfully');
      }
    } catch (err) {
      if (isMountedRef.current) {
        setSendApprovalError(err.message || 'Failed to send approval request');
        try {
          const refreshResult = await getApprovalStatus(settings, { requestId: proposal.approvalRequestId || approvalData?.requestId, proposalId: proposal.id });
          if (isMountedRef.current && refreshResult) { setApprovalData(refreshResult); persistApprovalSnapshot(refreshResult); }
        } catch (refreshErr) { console.warn('Failed to refresh status after send error', refreshErr); }
      }
    } finally {
      if (isMountedRef.current) setSendApprovalLoading(false);
      sendApprovalInProgressRef.current = false;
    }
  };

  const persistNewProposal = async (currentProposal) => {
    const newId = crypto.randomUUID();
    const now = Date.now();
    const newProposal = { ...currentProposal, id: newId, createdAt: now, updatedAt: now };
    await db.proposals.put(newProposal);
    lastSavedRef.current = JSON.stringify(newProposal);
    const currentSettings = await db.settings.get('global');
    if (currentSettings?.googleBackupConnected && currentSettings?.googleBackupUrl) {
      try {
        const res = await reserveDocumentNumber(currentSettings, newId, currentSettings.documentPrefix || 'SOW');
        if (res.documentNumber) {
          newProposal.documentNumber = res.documentNumber;
          newProposal.documentNumberAssignedAt = res.documentNumberAssignedAt || new Date().toISOString();
          await db.proposals.update(newId, { documentNumber: newProposal.documentNumber, documentNumberAssignedAt: newProposal.documentNumberAssignedAt });
        }
      } catch (err) { showToast(`SOW saved, but number reservation failed: ${err.message}`, 'error'); }
    }
    setProposal(newProposal);
    navigate(`/proposals/${newId}`, { replace: true });
    return newProposal;
  };

  const handleSaveDraft = async () => {
    if (saveDraftInProgressRef.current) return;
    if (operationInProgressRef.current) return;
    saveDraftInProgressRef.current = true;
    setIsSavingDraft(true);
    try {
      let currentProposal = proposal;
      if (isNewDocument) { currentProposal = await persistNewProposal(proposal); }
      else { const now = Date.now(); const updatedProp = { ...currentProposal, updatedAt: now }; await db.proposals.put(updatedProp); lastSavedRef.current = JSON.stringify(updatedProp); setProposal(updatedProp); }
      showToast('Draft saved');
    } catch (err) {
      showToast(`Failed to save draft: ${err.message}`, 'error');
    } finally {
      saveDraftInProgressRef.current = false;
      if (isMountedRef.current) setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (publishInProgressRef.current) return;
    publishInProgressRef.current = true;
    try {
      const isConfirmed = await confirm({ title: 'Publish Statement of Work?', message: 'This will lock the document as Published. You can reopen it later for amendments, which will increment the version number.', confirmText: 'Publish', cancelText: 'Keep as Draft' });
      if (!isConfirmed) { publishInProgressRef.current = false; return; }
      let currentProposal = proposal;
      if (isNewDocument) currentProposal = await persistNewProposal(proposal);
      const now = Date.now();
      const currentVersion = currentProposal.version;
      const newVersion = currentProposal.reopenedAt ? incrementMinorVersion(currentVersion) : currentVersion || '1.0';
      const updatedProp = { ...currentProposal, status: 'Published', publishedAt: currentProposal.publishedAt || now, updatedAt: now, version: newVersion };
      await db.proposals.put(updatedProp);
      lastSavedRef.current = JSON.stringify(updatedProp);
      setProposal(updatedProp);
      setIsPublishedView(true);
      showToast(`Published as ${newVersion}`);
      if (settings?.googleBackupConnected && settings?.googleBackupUrl) {
        try { const currentSettings = await db.settings.get('global'); await pushWorkspaceBackup(currentSettings); showToast('Synced to cloud'); }
        catch (err) { showToast(`Cloud sync skipped: ${err.message}`, 'error'); }
      }
    } catch (err) {
      showToast(`Publish failed: ${err.message}`, 'error');
    } finally {
      publishInProgressRef.current = false;
    }
  };

  const handleReopenAsDraft = async () => {
    const exactApproved = approvalOverview?.exactApproved || (approvalData && isValidApproved(approvalData) ? approvalData : null);
    if (exactApproved && isValidApproved(exactApproved)) {
      lastApprovedRequestIdRef.current = exactApproved.requestId || null;
      lastApprovedDocumentNumberRef.current = exactApproved.documentNumber || exactApproved.proposalDocumentNumber || null;
      lastApprovedVersionRef.current = exactApproved.documentVersion || exactApproved.version || null;
      lastApprovedDecisionAtRef.current = exactApproved.decisionAt || null;
    }
    const now = Date.now();
    const updatedProp = { ...proposal, status: 'Draft', reopenedAt: now, updatedAt: now };
    await db.proposals.put(updatedProp);
    lastSavedRef.current = JSON.stringify(updatedProp);
    setProposal(updatedProp);
    setIsPublishedView(false);
    setPublishedPromptOpen(false);
    showToast('Reopened as Draft');
  };

  const handleViewPublished = () => { setIsPublishedView(true); setPublishedPromptOpen(false); };

  const handleSaveAndSync = async () => {
    if (operationInProgressRef.current) return;
    operationInProgressRef.current = 'sync';
    clearOperationTimers();
    setOperationModalOpen(true);
    setOperationStatus('active');
    setOperationType('sync');
    setOperationProgress(0);
    setOperationElapsedSeconds(0);
    setOperationError(null);
    setOperationResult(null);
    setOperationMessage('');
    setOperationLocalSaved(false);
    operationElapsedTimerRef.current = setInterval(() => { if (isMountedRef.current) setOperationElapsedSeconds((prev) => prev + 1); }, 1000);
    let localSaved = false;
    try {
      let currentProposal = proposal;
      if (isNewDocument) { setOperationStage('Saving your Statement of Work'); setOperationDescription('Securing the latest proposal content in this browser.'); setOperationProgress(10); currentProposal = await persistNewProposal(proposal); }
      setOperationStage('Saving your Statement of Work');
      setOperationDescription('Securing the latest proposal content in this browser.');
      setOperationProgress(10);
      const updatedProp = { ...currentProposal, updatedAt: Date.now() };
      await db.proposals.put(updatedProp);
      lastSavedRef.current = JSON.stringify(updatedProp);
      localSaved = true;
      setOperationLocalSaved(true);
      if (settings?.googleBackupConnected && settings?.googleBackupUrl) {
        setOperationStage('Preparing the cloud workspace');
        setOperationDescription('Collecting proposals, snippets and settings.');
        setOperationProgress(35);
        const currentSettings = await db.settings.get('global');
        setOperationStage('Syncing with Google Sheets');
        setOperationDescription('Uploading the workspace to the connected cloud endpoint.');
        setOperationProgress(60);
        const result = await pushWorkspaceBackup(currentSettings);
        if (result.success) {
          setOperationStage('Verifying the cloud backup');
          setOperationDescription('Recording the confirmed sync timestamp.');
          setOperationProgress(90);
          const isoTimestamp = new Date().toISOString();
          await db.settings.update('global', { googleBackupLastSyncAt: isoTimestamp });
          setOperationStage('Workspace safely synced');
          setOperationDescription('Your Statement of Work is saved locally and backed up.');
          setOperationProgress(100);
          setOperationStatus('success');
          setOperationResult('Workspace safely synced');
          clearOperationTimers();
          showToast('Saved Locally & Synced to Cloud');
          operationSuccessTimeoutRef.current = setTimeout(() => { if (isMountedRef.current) { setOperationModalOpen(false); operationInProgressRef.current = null; } }, 1500);
        } else { throw new Error('Cloud sync did not return confirmed success.'); }
      } else {
        setOperationStage('Saved locally');
        setOperationDescription('Your changes are safe in this browser. Connect Google Sheets for cloud backup.');
        setOperationProgress(100);
        setOperationStatus('success');
        setOperationResult('Saved locally');
        clearOperationTimers();
        showToast('Saved Locally (Cloud sync not connected)');
        operationSuccessTimeoutRef.current = setTimeout(() => { if (isMountedRef.current) { setOperationModalOpen(false); operationInProgressRef.current = null; } }, 1500);
      }
    } catch (err) {
      console.error('Save & Sync failed:', err);
      clearOperationTimers();
      if (isMountedRef.current) {
        setOperationStatus('error');
        if (localSaved) setOperationError(new Error('Your proposal is saved locally, but cloud backup did not complete.'));
        else setOperationError(err);
        setOperationLocalSaved(localSaved);
        setOperationMessage('');
      }
      operationInProgressRef.current = null;
      if (localSaved) showToast(`Saved locally, but cloud sync failed: ${err.message}`, 'error');
      else showToast('Save failed', 'error');
    }
  };

  const handleDuplicate = async () => {
    setMoreMenuOpen(false);
    if (isNewDocument) { showToast('Please save the proposal before duplicating', 'error'); return; }
    const newId = crypto.randomUUID();
    const { documentNumber, documentNumberAssignedAt, documentVersion, version, lastProfessionalPdfAt, lastProfessionalPdfFileName, googleDocFileId, pdfFileId, googleDocUrl, pdfDriveUrl, generationId, publishedAt, reopenedAt, approval, approvalRequestId, lastApprovedRequestId, lastApprovedDocumentNumber, lastApprovedVersion, lastApprovedDecisionAt, ...rest } = proposal;
    const duplicatedProposal = { ...rest, id: newId, title: `${proposal.title || 'Untitled Proposal'} (Copy)`, status: 'Draft', createdAt: Date.now(), updatedAt: Date.now() };
    await db.proposals.add(duplicatedProposal);
    showToast('Proposal Duplicated');
    navigate(`/proposals/${newId}`);
  };

  const handleProfessionalPdf = async () => {
    setMoreMenuOpen(false);
    if (operationInProgressRef.current) return;
    operationInProgressRef.current = 'professionalPdf';
    clearOperationTimers();
    setOperationModalOpen(true);
    setOperationStatus('active');
    setOperationType('professionalPdf');
    setOperationProgress(0);
    setOperationElapsedSeconds(0);
    setOperationError(null);
    setOperationResult(null);
    setOperationMessage('');
    setOperationLocalSaved(false);
    operationElapsedTimerRef.current = setInterval(() => { if (isMountedRef.current) setOperationElapsedSeconds((prev) => prev + 1); }, 1000);
    try {
      if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null; }
      let currentProposal = proposal;
      if (isNewDocument) { setOperationStage('Preparing your Statement of Work'); setOperationDescription('Saving the latest proposal content.'); setOperationProgress(10); currentProposal = await persistNewProposal(proposal); }
      setOperationStage('Preparing your Statement of Work');
      setOperationDescription('Saving the latest proposal content.');
      setOperationProgress(10);
      const now = Date.now();
      const updatedProp = { ...currentProposal, updatedAt: now };
      await db.proposals.put(updatedProp);
      lastSavedRef.current = JSON.stringify(updatedProp);
      const latestSettings = await db.settings.get('global');
      if (!latestSettings?.googleBackupUrl || !latestSettings?.googleBackupConnected) throw new Error('Connect the Google Sheets workspace in Settings before generating a Drive Archive PDF.');
      setOperationStage('Confirming document identity');
      setOperationDescription('Checking the permanent SOW number.');
      setOperationProgress(25);
      let currentDocNumber = updatedProp.documentNumber;
      let currentAssignedAt = updatedProp.documentNumberAssignedAt;
      if (!currentDocNumber) { const res = await reserveDocumentNumber(latestSettings, updatedProp.id, latestSettings.documentPrefix || 'SOW'); if (res.documentNumber) { currentDocNumber = res.documentNumber; currentAssignedAt = res.documentNumberAssignedAt || new Date().toISOString(); } }
      setOperationStage('Loading BPA Solutions branding');
      setOperationDescription('Retrieving your business identity and logo.');
      setOperationProgress(38);
      const businessProfileForPdf = await getBusinessProfile(latestSettings, false);
      const proposalForGeneration = { ...updatedProp, documentNumber: currentDocNumber, documentNumberAssignedAt: currentAssignedAt };
      setOperationStage('Creating the professional document');
      setOperationDescription('Google Docs is formatting the document.');
      setOperationProgress(48);
      let msgIndex = 0;
      setOperationMessage(PDF_INFO_MESSAGES[0]);
      operationInfoMsgIntervalRef.current = setInterval(() => { if (isMountedRef.current) { msgIndex = (msgIndex + 1) % PDF_INFO_MESSAGES.length; setOperationMessage(PDF_INFO_MESSAGES[msgIndex]); } }, 4500);
      let estProgress = 48;
      operationEstProgressIntervalRef.current = setInterval(() => { if (isMountedRef.current && estProgress < 88) { estProgress += 1; setOperationProgress(estProgress); } }, 2000);
      const result = await generateProfessionalPdf(latestSettings, proposalForGeneration, businessProfileForPdf);
      clearInterval(operationInfoMsgIntervalRef.current);
      operationInfoMsgIntervalRef.current = null;
      clearInterval(operationEstProgressIntervalRef.current);
      operationEstProgressIntervalRef.current = null;
      if (!result.pdfBase64 || !result.fileName || !result.documentNumber) throw new Error('Server did not return a valid PDF file or document number.');
      setOperationStage('Finalising the PDF');
      setOperationDescription('Preparing the client-ready file.');
      setOperationProgress(92);
      setOperationMessage('');
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      setOperationStage('Downloading the PDF');
      setOperationDescription('Transferring the finished document to your device.');
      setOperationProgress(97);
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      const metaUpdate = { documentNumber: result.documentNumber, lastProfessionalPdfAt: Date.now(), lastProfessionalPdfFileName: result.fileName, googleDocFileId: result.googleDocFileId, pdfFileId: result.pdfFileId, googleDocUrl: result.googleDocUrl, pdfDriveUrl: result.pdfDriveUrl, generationId: result.generationId, updatedAt: Date.now() };
      if (!currentAssignedAt) metaUpdate.documentNumberAssignedAt = new Date().toISOString();
      await db.proposals.update(updatedProp.id, metaUpdate);
      setProposal((prev) => { if (!prev || prev.id !== updatedProp.id) return prev; return { ...prev, ...metaUpdate }; });
      setOperationStage('Professional PDF ready');
      setOperationDescription('Your client-ready Statement of Work has been generated successfully.');
      setOperationProgress(100);
      setOperationStatus('success');
      setOperationResult({ documentNumber: result.documentNumber, fileName: result.fileName });
      clearOperationTimers();
      showToast(`Drive Archive PDF generated: ${result.documentNumber}`);
      operationSuccessTimeoutRef.current = setTimeout(() => { if (isMountedRef.current) { setOperationModalOpen(false); operationInProgressRef.current = null; } }, 1500);
    } catch (err) {
      console.error('Professional PDF generation failed:', err);
      clearOperationTimers();
      clearInterval(operationInfoMsgIntervalRef.current);
      operationInfoMsgIntervalRef.current = null;
      clearInterval(operationEstProgressIntervalRef.current);
      operationEstProgressIntervalRef.current = null;
      if (isMountedRef.current) { setOperationStatus('error'); setOperationError(err); setOperationMessage(''); }
      operationInProgressRef.current = null;
      showToast(`Drive Archive PDF failed: ${err.message}`, 'error');
    }
  };

  const handleOperationRetry = () => {
    clearOperationTimers();
    const retryType = operationType;
    setOperationModalOpen(false);
    setOperationStatus('active');
    setOperationError(null);
    setOperationResult(null);
    setOperationMessage('');
    setOperationElapsedSeconds(0);
    setOperationProgress(0);
    setOperationLocalSaved(false);
    setTimeout(() => { if (retryType === 'sync') handleSaveAndSync(); else handleProfessionalPdf(); }, 50);
  };

  const handleOperationCloseError = () => { clearOperationTimers(); setOperationModalOpen(false); operationInProgressRef.current = null; };

  const formatHtmlToPlainText = (html) => {
    if (!html || html === '<p></p>') return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.querySelectorAll('ul').forEach((ul) => { ul.querySelectorAll('li').forEach((li) => { li.prepend('• '); li.append('\n'); }); ul.append('\n'); });
    tempDiv.querySelectorAll('ol').forEach((ol) => { ol.querySelectorAll('li').forEach((li, index) => { li.prepend(`${index + 1}. `); li.append('\n'); }); ol.append('\n'); });
    tempDiv.querySelectorAll('p,div,h1,h2,h3,h4,h5,h6').forEach((el) => { el.append('\n'); });
    return tempDiv.innerText.trim().replace(/\n{3,}/g, '\n\n');
  };

  const handleCopyClientSummary = async () => {
    setMoreMenuOpen(false);
    const currencySym = settings.currency === 'AUD' || settings.currency === 'USD' ? '$' : settings.currency;
    const formattedTotal = total.toLocaleString(undefined, { minimumFractionDigits: 2 });
    let investmentHtml = `<p><strong>Total: ${currencySym}${formattedTotal} (inc ${settings.taxName})</strong></p><ul>`;
    proposal.paymentMilestones.forEach((m) => { const mAmount = total * (m.percent / 100); investmentHtml += `<li>${m.label} (${m.percent}%): ${currencySym}${mAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</li>`; });
    investmentHtml += `</ul>`;
    const sections = [
      { label: 'In Scope', content: proposal.sections.inScope },
      { label: 'Out of Scope', content: proposal.sections.exclusions },
      { label: 'Commercial Investment', content: investmentHtml },
      { label: 'Commercial / Payment Notes', content: proposal.commercialNotes },
      { label: 'Change Control', content: proposal.sections.changeControl },
      { label: 'Next Steps', content: proposal.sections.nextSteps },
    ];
    let html = `<div style="font-family: Arial, sans-serif; color: #333;"><h2>${proposal.title}</h2>`;
    let plainText = `${proposal.title}\n\n`;
    sections.forEach((sec) => { if (sec.content && sec.content !== '<p></p>') { html += `<h3 style="color: #4338ca; border-bottom: 1px solid #eee; padding-bottom: 4px;">${sec.label}</h3><div>${sec.content}</div>`; plainText += `${sec.label.toUpperCase()}\n${formatHtmlToPlainText(sec.content)}\n\n`; } });
    html += `</div>`;
    try {
      const blobHtml = new Blob([html], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });
      const data = [new window.ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText })];
      await navigator.clipboard.write(data);
      showToast('Client summary copied');
    } catch (err) {
      showToast('Failed to copy summary', 'error');
    }
  };

  const handleCopyMilestones = () => {
    const currencySym = settings.currency === 'AUD' || settings.currency === 'USD' ? '$' : settings.currency;
    const formattedTotal = total.toLocaleString(undefined, { minimumFractionDigits: 2 });
    let text = `PAYMENT MILESTONES\n\nTotal: ${currencySym}${formattedTotal}\n\n`;
    proposal.paymentMilestones.forEach((m) => { const mAmount = total * (m.percent / 100); text += `${m.label} (${m.percent}%): ${currencySym}${mAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}\n`; });
    text += `\nPayment Terms:\nWork will commence once the deposit has been received.`;
    navigator.clipboard.writeText(text);
    showToast('Milestones copied to clipboard');
  };

  const subtotal = proposal?.financials?.items?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) || 0;
  const taxRate = settings?.taxRate || 10;
  const taxAmount = proposal?.financials?.taxEnabled ? subtotal * (taxRate / 100) : 0;
  const total = subtotal + taxAmount;
  const currency = settings?.currency || 'AUD';
  const totalMilestonePercent = proposal?.paymentMilestones?.reduce((sum, m) => sum + (Number(m.percent) || 0), 0) || 0;

  const openPremiumPdfPrintView = async () => {
    setMoreMenuOpen(false);
    let printTab = null;
    try {
      printTab = window.open('', '_blank');
      if (!printTab) { showToast('Please allow pop-ups to generate a Print PDF.', 'error'); return; }
      printTab.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"><title>Preparing Print PDF…</title><style>body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background: #f5f5f5;display: flex;align-items: center;justify-content: center;min-height: 100vh;margin: 0;color: #444;} .loader {text-align: center;} .loader svg {width: 48px;height: 48px;animation: spin 1s linear infinite;margin-bottom: 16px;} @keyframes spin {to {transform: rotate(360deg);}} .loader p {font-size: 14px;font-weight: 600;}</style></head><body><div class="loader"><svg viewBox="0 0 24 24" fill="none" stroke="#4f46e5" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="#4f46e5"/></svg><p>Preparing your print-ready PDF…</p></div></body></html>`);
      printTab.document.close();
      const approvalEvidence = await resolveApprovedExportEvidence();
      const docHtml = buildProposalExportHtml({ proposal, settings, removeSpacing, subtotal, taxAmount, total, currency, businessProfile, approvalEvidence: null });
      const safeTitle = (proposal.title || 'Statement of Work').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const fullHtml = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"><title>${safeTitle} — Print PDF</title><style> body {margin: 0;padding: 0;background: #f5f5f5;font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif;} .premium-print-instructions {max-width: 800px;margin: 24px auto;padding: 16px 24px;background: #eef2ff;border: 1px solid #c7d2fe;border-radius: 12px;font-size: 13px;color: #3730a3;line-height: 1.6;} .premium-print-instructions strong {font-weight: 700;} .premium-doc-wrapper {max-width: 800px;margin: 0 auto 40px;background: #ffffff;box-shadow: 0 4px 24px rgba(0,0,0,0.08);} .export-doc {font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif;line-height: 1.5;color: #172033;background: #ffffff;padding: 40px 48px;width: 800px;box-sizing: border-box;font-size: 12px;} .sow-content p {margin: 0 0 6px 0 !important;} .sow-content ul {list-style-type: disc !important;list-style-position: outside !important;margin: 6px 0 !important;padding-left: 22px !important;color: inherit !important;} .sow-content ol {list-style-type: decimal !important;list-style-position: outside !important;margin: 6px 0 !important;padding-left: 26px !important;color: inherit !important;} .sow-content li {display: list-item !important;margin-bottom: 3px !important;padding-left: 6px !important;font-size: inherit !important;line-height: inherit !important;} .sow-content h3 {margin: 10px 0 4px 0 !important;font-size: 12px !important;font-weight: bold !important;color: #172033 !important;} .sow-content h4 {margin: 8px 0 3px 0 !important;font-size: 11px !important;font-weight: bold !important;} .sow-content a {word-break: break-word;} .commercial-notes-container p {margin-bottom: 2px !important;} .print-avoid-break {break-inside: avoid;page-break-inside: avoid;} thead {display: table-header-group;} tr {break-inside: avoid;page-break-inside: avoid;} img {max-width: 100% !important;} @media print {@page {size: A4;margin: 12mm;} .export-doc {width: 100% !important;padding: 0 !important;box-shadow: none !important;} body {background: #ffffff !important;margin: 0;padding: 0;} .premium-print-instructions {display: none !important;} .premium-doc-wrapper {box-shadow: none !important;margin: 0 !important;max-width: 100% !important;} * {-webkit-print-color-adjust: exact !important;print-color-adjust: exact !important;}} </style></head><body> <div class="premium-print-instructions"> <strong>Choose "Save as PDF" as the printer destination.</strong><br/> Enable <strong>"Background graphics"</strong> for the complete premium brand treatment. </div> <div class="premium-doc-wrapper">${docHtml}</div> </body></html>`;
      printTab.document.open();
      printTab.document.write(fullHtml);
      printTab.document.close();
      await new Promise((resolve) => { const imgs = printTab.document.querySelectorAll('img'); if (imgs.length === 0) return resolve(); let remaining = imgs.length; const onSettle = () => { remaining--; if (remaining <= 0) resolve(); }; imgs.forEach((img) => { if (img.complete) onSettle(); else { img.addEventListener('load', onSettle, { once: true }); img.addEventListener('error', onSettle, { once: true }); } }); });
      if (printTab.document.fonts && printTab.document.fonts.ready) { try { await printTab.document.fonts.ready; } catch (e) {} }
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 200));
      printTab.focus();
      printTab.print();
    } catch (err) {
      console.error('Print PDF failed:', err);
      showToast(`Print PDF failed: ${err.message}`, 'error');
      if (printTab && !printTab.closed) { try { printTab.document.body.innerHTML = `<div style="font-family: sans-serif;text-align: center;padding: 40px;color: #b91c1c;"><h2>PDF generation failed</h2><p>${err.message}</p></div>`; } catch (e) {} }
    }
  };

  const generatePremiumPdfDownload = async () => {
    setMoreMenuOpen(false);
    if (premiumPdfInProgressRef.current) return;
    premiumPdfInProgressRef.current = true;
    setPremiumPdfInProgress(true);
    let sandbox = null;
    let pageCanvases = [];
    try {
      const approvalEvidence = await resolveApprovedExportEvidence();
      if (approvalEvidence) { await generateApprovedPdfFromOriginalSnapshot(approvalEvidence); return; }
      const staleNode = document.getElementById('premium-pdf-export-node');
      if (staleNode && staleNode.parentNode) staleNode.parentNode.removeChild(staleNode);
      const hadLocalApproval = approvalData?.status === 'APPROVED' || proposal.approval?.status === 'APPROVED';
      const sourceWrapper = document.createElement('div');
      sourceWrapper.id = 'premium-pdf-export-node';
      sourceWrapper.style.position = 'absolute';
      sourceWrapper.style.top = '0';
      sourceWrapper.style.left = '-9999px';
      sourceWrapper.style.background = '#ffffff';
      sourceWrapper.style.pointerEvents = 'none';
      sourceWrapper.style.zIndex = '-9999';
      sourceWrapper.innerHTML = buildProposalExportHtml({ proposal, settings, removeSpacing, subtotal, taxAmount, total, currency, businessProfile, approvalEvidence: null });
      document.body.appendChild(sourceWrapper);
      const sourceExportDoc = sourceWrapper.querySelector('.export-doc');
      if (!sourceExportDoc) throw new Error('Premium PDF document element not found.');
      await waitForAssets(sourceExportDoc);
      sandbox = createPremiumPdfPaginationSandbox();
      let pages = await paginatePremiumDocumentDom(sourceExportDoc, sandbox);
      await waitForAssets(sandbox);
      let needsRepagination = false;
      for (const page of pages) { if (doesPdfPageOverflow(page.pageContentElement)) { needsRepagination = true; break; } }
      if (needsRepagination) { for (const page of pages) { if (page.pageElement.parentNode) sandbox.removeChild(page.pageElement); } pages = await paginatePremiumDocumentDom(sourceExportDoc, sandbox); await waitForAssets(sandbox); }
      try { validatePaginatedDocumentCompleteness(sourceExportDoc, pages); } catch (validationErr) { console.error('Pagination validation failed:', validationErr); showToast('Premium PDF could not be paginated safely. Please use Print PDF (Selectable Text) and report this document.', 'error'); return; }
      const sourceBlocks = Array.from(sourceExportDoc.children).filter((el) => el.tagName !== 'STYLE');
      sourceBlocks.forEach((b) => b.removeAttribute('data-pdf-source-id'));
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfPageWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < pages.length; i++) {
        const pageElement = pages[i].pageElement;
        const canvas = await html2canvas(pageElement, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff', logging: false, width: PAGE_WIDTH, height: PAGE_HEIGHT, windowWidth: PAGE_WIDTH, windowHeight: PAGE_HEIGHT });
        pageCanvases.push(canvas);
        const imgData = canvas.toDataURL('image/jpeg', 0.96);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfPageWidth, pdfPageHeight, undefined, 'FAST');
      }
      const safeFilename = sanitizeFilename(proposal.title);
      pdf.save(`${safeFilename}_Premium_SOW.pdf`);
      showToast(hadLocalApproval ? 'The PDF was exported without an approval mark because approval for this exact version could not be verified.' : 'Premium PDF downloaded successfully.');
    } catch (err) {
      console.error('Premium PDF generation failed:', err);
      showToast('Premium PDF generation failed. Please try again.', 'error');
    } finally {
      pageCanvases.forEach((c) => { c.width = 0; c.height = 0; });
      pageCanvases = [];
      if (sandbox && sandbox.parentNode) sandbox.parentNode.removeChild(sandbox);
      const oldNode = document.getElementById('premium-pdf-export-node');
      if (oldNode && oldNode.parentNode) oldNode.parentNode.removeChild(oldNode);
      premiumPdfInProgressRef.current = false;
      if (isMountedRef.current) setPremiumPdfInProgress(false);
      setMoreMenuOpen(false);
    }
  };

  const generatePNG = async () => {
    setMoreMenuOpen(false);
    showToast('Preparing PNG export...');
    const staleNode = document.getElementById('temp-export-node');
    if (staleNode && staleNode.parentNode) staleNode.parentNode.removeChild(staleNode);
    const tempNode = document.createElement('div');
    tempNode.id = 'temp-export-node';
    tempNode.style.position = 'absolute';
    tempNode.style.top = '0';
    tempNode.style.left= '0';
    tempNode.style.zIndex = '-9999';
    tempNode.style.pointerEvents = 'none';
    tempNode.style.background = '#ffffff';
    tempNode.innerHTML = buildProposalExportHtml({ proposal, settings, removeSpacing, subtotal, taxAmount, total, currency, businessProfile });
    document.body.appendChild(tempNode);
    const exportDocEl = tempNode.querySelector('.export-doc');
    if (!exportDocEl) { showToast('PNG export failed: document element not found.', 'error'); if (tempNode.parentNode) tempNode.parentNode.removeChild(tempNode); return; }
    try {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 300));
      const imgs = exportDocEl.querySelectorAll('img');
      if (imgs.length > 0) {
        await new Promise((resolve) => {
          let remaining = imgs.length;
          const onSettle = () => { remaining--; if (remaining <= 0) resolve(); };
          imgs.forEach((img) => { if (img.complete) onSettle(); else { img.addEventListener('load', onSettle, { once: true }); img.addEventListener('error', onSettle, { once: true }); } });
        });
      }
      if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch (e) {} }
      const renderedWidth = exportDocEl.offsetWidth;
      const renderedHeight = exportDocEl.offsetHeight;
      const canvas = await html2canvas(exportDocEl, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: renderedWidth, height: renderedHeight, logging: false, windowWidth: renderedWidth });
      canvas.toBlob((blob) => {
        if (!blob) { showToast('PNG export failed', 'error'); if (tempNode.parentNode) tempNode.parentNode.removeChild(tempNode); return; }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const sanitizedTitle = (proposal.title || 'Untitled_Proposal').replace(/\s+/g, '_');
        link.download = `${sanitizedTitle}_SOW.png`;
        link.href = url;
        link.click();
        setTimeout(() => { URL.revokeObjectURL(url); if (tempNode.parentNode) tempNode.parentNode.removeChild(tempNode); showToast('PNG Exported Successfully'); }, 500);
      }, 'image/png');
    } catch (err) {
      console.error('PNG generation error:', err);
      showToast('PNG generation failed', 'error');
      if (tempNode.parentNode) tempNode.parentNode.removeChild(tempNode);
    }
  };

  const exportProfessionalHTML = () => {
    setMoreMenuOpen(false);
    const docHTML = buildProposalExportHtml({ proposal, settings, removeSpacing, subtotal, taxAmount, total, currency, businessProfile });
    const safeTitle = (proposal.title || 'Untitled').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const fullHTML = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${safeTitle}</title></head><body>${docHTML}</body></html>`;
    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(proposal.title || 'Untitled').replace(/\s+/g, '_')}_Final.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Professional HTML Exported');
  };

  const renderEditorSection = (title, key, icon, colorClass, borderClass, textColorClass = 'text-white') => (
    <section className={`bg-white rounded-2xl border ${borderClass} shadow-sm overflow-visible`}>
      <div className={`${colorClass} rounded-t-[15px] px-6 py-4 ${textColorClass} flex items-center gap-3 font-black text-xs uppercase tracking-widest`}>
        <SafeIcon icon={icon} />
        {title}
      </div>
      <div className="p-6">
        <RichTextEditor value={proposal.sections[key] || ''} onChange={(val) => handleSectionChange(key, val)} sectionType={key} />
      </div>
    </section>
  );

  if (!proposal || !settings) return (
    <div className="p-8 font-black uppercase text-gray-400 text-center flex flex-col items-center justify-center h-full gap-4">
      <SafeIcon icon={FiIcons.FiRefreshCcw} className="text-4xl animate-spin text-indigo-200" />
      Synchronizing SOW...
    </div>
  );

  const isSyncActive = operationModalOpen && operationType === 'sync' && operationStatus === 'active';
  const isPdfActive = operationModalOpen && operationType === 'professionalPdf' && operationStatus === 'active';
  const proposalIsPublished = isPublishedStatus(proposal.status);
  const statusLabel = canonicalStatus(proposal.status);

  const isApprovalApproved =
    (approvalOverview?.exactApproved && isValidApproved(approvalOverview.exactApproved) && isExactApprovalMatch(approvalOverview.exactApproved, proposal.documentNumber, proposal.version)) ||
    (approvalData && isValidApproved(approvalData) && isExactApprovalMatch(approvalData, proposal.documentNumber, proposal.version));

  const isApprovalPending =
    !isApprovalApproved &&
    approvalOverview?.latestExactRequest?.status === 'PENDING' &&
    isExactApprovalMatch(approvalOverview.latestExactRequest, proposal.documentNumber, proposal.version);

  const historicalApprovedRecord =
    approvalOverview?.latestApproved &&
    isValidApproved(approvalOverview.latestApproved) &&
    approvalOverview.latestApproved.documentNumber === proposal.documentNumber &&
    normaliseApprovalVersion(approvalOverview.latestApproved.documentVersion || approvalOverview.latestApproved.version) !== normaliseApprovalVersion(proposal.version)
      ? approvalOverview.latestApproved
      : null;

  const moreMenuItems = isPublishedView
    ? [
        ...(!isApprovalPending ? [{ label: 'Send for Approval', icon: FiIcons.FiSend, action: handleSendForApproval, disabled: isApprovalApproved }] : []),
        { label: 'Premium PDF', icon: FiIcons.FiDownload, action: generatePremiumPdfDownload, disabled: premiumPdfInProgress },
        { label: 'Print PDF (Selectable Text)', icon: FiIcons.FiPrinter, action: openPremiumPdfPrintView },
        { label: 'Drive Archive PDF', icon: FiIcons.FiAward, action: handleProfessionalPdf, active: isPdfActive },
        { label: 'PNG', icon: FiIcons.FiImage, action: generatePNG },
        { label: 'Export HTML', icon: FiIcons.FiDownload, action: exportProfessionalHTML },
        { label: 'Reopen as Draft', icon: FiIcons.FiEdit2, action: handleReopenAsDraft },
      ]
    : [
        { label: 'Save Draft', icon: FiIcons.FiSave, action: handleSaveDraft },
        { label: 'Duplicate', icon: FiIcons.FiCopy, action: handleDuplicate },
        { label: 'Copy Summary', icon: FiIcons.FiLayout, action: handleCopyClientSummary },
        ...(!isApprovalPending ? [{ label: 'Send for Approval', icon: FiIcons.FiSend, action: handleSendForApproval, disabled: isApprovalApproved }] : []),
        { label: 'Premium PDF', icon: FiIcons.FiDownload, action: generatePremiumPdfDownload, disabled: premiumPdfInProgress },
        { label: 'Print PDF (Selectable Text)', icon: FiIcons.FiPrinter, action: openPremiumPdfPrintView },
        { label: 'Drive Archive PDF', icon: FiIcons.FiAward, action: handleProfessionalPdf, active: isPdfActive },
        { label: 'PNG', icon: FiIcons.FiImage, action: generatePNG },
        { label: 'Export HTML', icon: FiIcons.FiDownload, action: exportProfessionalHTML },
        { label: 'Publish', icon: FiIcons.FiLock, action: handlePublish },
      ];

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      <AnimatePresence>
        {publishedPromptOpen && (
          <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }} className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
            <motion.div initial={prefersReducedMotion ? { opacity: 1 } : { scale: 0.95, opacity: 0 }} animate={prefersReducedMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }} exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0.95, opacity: 0 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden mx-auto" role="dialog" aria-modal="true">
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-xl shrink-0">🛡️</div>
                  <h2 className="text-xl font-black text-gray-900">Published Document</h2>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">This Statement of Work is published and locked. Do you want to reopen it for amendments?</p>
              </div>
              <div className="bg-gray-50 px-8 py-5 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100">
                <button onClick={handleViewPublished} className="px-5 py-2.5 text-xs font-black text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 uppercase tracking-widest transition-all order-2 sm:order-1 sm:mr-auto">View Published Version</button>
                <button onClick={handleReopenAsDraft} className="px-6 py-2.5 text-xs font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all order-1 sm:order-2">Reopen as Draft</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <OperationProgressModal open={operationModalOpen} status={operationStatus} type={operationType} stage={operationStage} description={operationDescription} progress={operationProgress} message={operationMessage} elapsedSeconds={operationElapsedSeconds} error={operationError} result={operationResult} localSaved={operationLocalSaved} prefersReducedMotion={prefersReducedMotion} onCloseError={handleOperationCloseError} onRetry={handleOperationRetry} />

      <SendForApprovalModal open={sendApprovalModalOpen} proposal={proposal} loading={sendApprovalLoading} error={sendApprovalError} onClose={() => setSendApprovalModalOpen(false)} onSubmit={handleSubmitApproval} />

      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between shrink-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button onClick={() => navigate('/proposals')} className="text-gray-400 hover:text-gray-600 transition-colors shrink-0">
            <SafeIcon icon={FiIcons.FiArrowLeft} className="text-xl" />
          </button>
          <div className="flex flex-col min-w-0 flex-1">
            {isPublishedView ? (
              <p className="text-xl sm:text-2xl font-black text-gray-900 truncate">{proposal.title}</p>
            ) : (
              <input type="text" value={proposal.title} onChange={(e) => handleChange('title', e.target.value)} className="text-xl sm:text-2xl font-black text-gray-900 border-none outline-none focus:ring-0 bg-transparent w-full truncate" />
            )}
            <div className="flex items-center gap-2">
              {proposal.documentNumber ? (
                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest truncate">{proposal.documentNumber}</span>
              ) : (
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest italic truncate">{isNewDocument ? 'Save to assign SOW number' : 'Document number pending'}</span>
              )}
              <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${proposalIsPublished ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{statusLabel}</span>
            </div>
          </div>
        </div>

        <div className="hidden xl:flex items-center gap-3 flex-wrap justify-end">
          {!isPublishedView && (
            <>
              <button onClick={handleDuplicate} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-slate-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 uppercase tracking-wider transition-all">
                <SafeIcon icon={FiIcons.FiCopy} /> Duplicate
              </button>
              <button onClick={handleCopyClientSummary} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100 uppercase tracking-wider transition-all">
                <SafeIcon icon={FiIcons.FiLayout} /> Copy Summary
              </button>
              <button onClick={handleSaveDraft} disabled={isSavingDraft || operationModalOpen} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-slate-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                <SafeIcon icon={isSavingDraft ? FiIcons.FiRefreshCw : FiIcons.FiSave} className={isSavingDraft ? 'animate-spin' : ''} />
                {isSavingDraft ? 'Saving...' : 'Save Draft'}
              </button>
              <button onClick={handleSaveAndSync} disabled={operationModalOpen} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl hover:bg-emerald-100 uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                <SafeIcon icon={isSyncActive ? FiIcons.FiRefreshCw : FiIcons.FiCloudLightning} className={isSyncActive ? 'animate-spin' : ''} />
                {isSyncActive ? 'Syncing...' : 'Save & Sync'}
              </button>
            </>
          )}
          {!isApprovalPending && (
            <button onClick={handleSendForApproval} disabled={isApprovalApproved} title={isApprovalApproved ? 'Reopen and publish a new version to send another approval request' : ''} className={`flex items-center gap-2 px-4 py-2.5 text-xs font-black text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100 uppercase tracking-wider transition-all ${isApprovalApproved ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <SafeIcon icon={FiIcons.FiSend} /> Send for Approval
            </button>
          )}
          <button onClick={generatePremiumPdfDownload} disabled={premiumPdfInProgress} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-white bg-indigo-600 border border-indigo-700 rounded-xl hover:bg-indigo-700 uppercase tracking-wider transition-all shadow-lg shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed" title="Download a premium PDF matching the on-screen preview.">
            <SafeIcon icon={premiumPdfInProgress ? FiIcons.FiRefreshCw : FiIcons.FiDownload} className={premiumPdfInProgress ? 'animate-spin' : ''} />
            {premiumPdfInProgress ? 'Generating…' : 'Premium PDF'}
          </button>
          <button onClick={generatePNG} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-slate-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 uppercase tracking-wider transition-all">
            <SafeIcon icon={FiIcons.FiImage} /> PNG
          </button>
          <button onClick={exportProfessionalHTML} className="flex items-center gap-2 px-6 py-2.5 text-xs font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-200 uppercase tracking-wider transition-all">
            <SafeIcon icon={FiIcons.FiDownload} /> Export
          </button>
          {!isPublishedView && !proposalIsPublished && (
            <button onClick={handlePublish} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-white bg-green-600 rounded-xl hover:bg-green-700 uppercase tracking-wider shadow-lg shadow-green-200 transition-all">
              <SafeIcon icon={FiIcons.FiLock} /> Publish
            </button>
          )}
          {isPublishedView && (
            <button onClick={handleReopenAsDraft} className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-white bg-amber-600 rounded-xl hover:bg-amber-700 uppercase tracking-wider shadow-lg shadow-amber-200 transition-all">
              <SafeIcon icon={FiIcons.FiEdit2} /> Reopen as Draft
            </button>
          )}
        </div>

        <div className="flex xl:hidden items-center gap-2">
          {!isPublishedView && (
            <button onClick={handleSaveAndSync} disabled={operationModalOpen} className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg hover:bg-emerald-100 uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              <SafeIcon icon={isSyncActive ? FiIcons.FiRefreshCw : FiIcons.FiCloudLightning} className={isSyncActive ? 'animate-spin' : ''} />
              {isSyncActive ? 'Syncing' : 'Sync'}
            </button>
          )}
          <div className="relative" ref={moreMenuRef}>
            <button onClick={() => setMoreMenuOpen(!moreMenuOpen)} className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-black text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg hover:bg-indigo-100 uppercase tracking-wider transition-all">
              <SafeIcon icon={FiIcons.FiMoreHorizontal} /> More
            </button>
            {moreMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-[280px] bg-white border border-gray-200 shadow-2xl rounded-xl z-[150] overflow-hidden">
                <div className="py-1">
                  {moreMenuItems.map((item, idx) => (
                    <button key={idx} onClick={item.action} disabled={(operationModalOpen && item.active) || item.disabled || (item.label === 'Premium PDF' && premiumPdfInProgress)} className="w-full text-left px-4 py-3 text-xs font-bold text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-3 border-b border-gray-50 last:border-0 disabled:opacity-50 disabled:cursor-not-allowed">
                      <SafeIcon icon={item.icon} className={item.active || (item.label === 'Premium PDF' && premiumPdfInProgress) ? 'animate-spin' : ''} />
                      {item.label === 'Premium PDF' && premiumPdfInProgress ? 'Generating…' : item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative" ref={containerRef}>
        {!isPublishedView && (
          <div style={{ width: `calc(var(--split-width,${leftWidth}%) - 3px)` }} className="overflow-y-auto px-4 sm:px-8 py-6 sm:py-10 bg-gray-50 flex-grow xl:flex-none editor-left-panel">
            <div className="max-w-4xl mx-auto space-y-6 pb-40">
              {!isNewDocument && (
                <ApprovalStatusCard
                  approval={approvalData}
                  isLoading={approvalLoading}
                  onRefresh={handleRefreshApproval}
                  onResend={handleResendApproval}
                  onRevoke={handleRevokeApproval}
                  onDownloadApprovedPdf={generatePremiumPdfDownload}
                  approvedPdfInProgress={premiumPdfInProgress}
                  onEmailApprovedPdfToClient={handleEmailApprovedPdfToClient}
                  approvedPdfEmailInProgress={approvedPdfEmailInProgress}
                  approvedPdfEmailStage={approvedPdfEmailStage}
                  approvalOverview={approvalOverview}
                  currentDocumentNumber={proposal.documentNumber}
                  currentDocumentVersion={proposal.version}
                  documentStatus={proposal.status}
                  onRestoreApprovedVersion={handleRestoreApprovedVersion}
                  restoreApprovalInProgress={restoreApprovalInProgress}
                  onRecoverUnchangedApprovedVersion={handleRecoverUnchangedApprovedVersion}
                  recoverUnchangedInProgress={recoverUnchangedInProgress}
                  onDownloadHistoricalApprovedPdf={handleDownloadHistoricalApprovedPdf}
                  historicalPdfDownloadInProgress={historicalPdfDownloadInProgress}
                />
              )}

              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-200 shadow-sm grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Client Organization</label>
                  <input type="text" value={proposal.clientName} onChange={(e) => handleChange('clientName', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Client Name" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Client Email</label>
                  <input type="email" value={proposal.emailAddress} onChange={(e) => handleChange('emailAddress', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="client@example.com" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Primary Stakeholder</label>
                  <input type="text" value={proposal.sections.pointOfContact} onChange={(e) => handleSectionChange('pointOfContact', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Point of Contact" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Client Mobile</label>
                  <input type="tel" inputMode="tel" autoComplete="tel" value={proposal.clientMobile} onChange={(e) => handleChange('clientMobile', e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0425 228 778 or +61 425 228 778" />
                  <p className="text-[9px] text-gray-400 mt-1 font-medium">Enter an Australian mobile number using 04, 61 or +61 format. This number will be reused when sending the SOW for approval.</p>
                </div>
              </div>

              {renderEditorSection('1. Operational Scope', 'inScope', FiIcons.FiCheckCircle, 'bg-indigo-600', 'border-gray-200')}
              {renderEditorSection('2. Explicit Exclusions', 'exclusions', FiIcons.FiXOctagon, 'bg-red-600', 'border-red-100')}

              <ProposalFinancials proposal={proposal} settings={settings} currency={currency} total={total} subtotal={subtotal} taxAmount={taxAmount} totalMilestonePercent={totalMilestonePercent} showPresets={showPresets} onTogglePresets={() => setShowPresets(!showPresets)} onApplyPreset={applyPreset} onTaxToggle={handleTaxToggle} onFinancialChange={handleFinancialChange} onAddFinancialItem={addFinancialItem} onRemoveFinancialItem={removeFinancialItem} onMilestoneChange={handleMilestoneChange} onAddMilestone={addMilestone} onRemoveMilestone={removeMilestone} onCopyMilestones={handleCopyMilestones} onCommercialNotesChange={handleCommercialNotesChange} />

              {renderEditorSection('4. Change Control', 'changeControl', FiIcons.FiShield, 'bg-slate-700', 'border-gray-200')}
              {renderEditorSection('Next Steps', 'nextSteps', FiIcons.FiArrowRightCircle, 'bg-indigo-600', 'border-indigo-100')}
              {renderEditorSection('5. Detailed Deliverables', 'deliverables', FiIcons.FiBox, 'bg-slate-600', 'border-gray-200')}
              {renderEditorSection('6. SLAs & Support Timings', 'slas', FiIcons.FiClock, 'bg-slate-600', 'border-gray-200')}
              {renderEditorSection('7. Revisions & Approvals', 'revisionLimits', FiIcons.FiEdit, 'bg-slate-600', 'border-gray-200')}
              {renderEditorSection('8. Client Dependencies', 'clientDependencies', FiIcons.FiUsers, 'bg-slate-600', 'border-gray-200')}
              {renderEditorSection('9. Additional Terms & Conditions', 'terms', FiIcons.FiFileText, 'bg-slate-600', 'border-gray-200')}
            </div>
          </div>
        )}

        {!isPublishedView && (
          <div onMouseDown={startResizing} onDoubleClick={() => { setLeftWidth(60); localStorage.setItem('scopeshield_split_ratio', '60'); }} className={`w-1.5 h-full bg-gray-200 hover:bg-indigo-400 cursor-col-resize z-30 transition-colors hidden xl:flex items-center justify-center group flex-shrink-0 ${isResizing ? 'bg-indigo-500 w-2' : ''}`} title="Drag to resize (Double-click to reset)">
            <div className={`w-0.5 h-10 bg-gray-300 group-hover:bg-white rounded-full ${isResizing ? 'bg-white h-16' : ''}`}></div>
          </div>
        )}

        {isPublishedView && !isNewDocument && (
          <div className="absolute top-4 right-4 z-20 w-[340px] max-w-[calc(100%-2rem)]">
            <ApprovalStatusCard
              approval={approvalData}
              isLoading={approvalLoading}
              onRefresh={handleRefreshApproval}
              onResend={handleResendApproval}
              onRevoke={handleRevokeApproval}
              onDownloadApprovedPdf={generatePremiumPdfDownload}
              approvedPdfInProgress={premiumPdfInProgress}
              onEmailApprovedPdfToClient={handleEmailApprovedPdfToClient}
              approvedPdfEmailInProgress={approvedPdfEmailInProgress}
              approvedPdfEmailStage={approvedPdfEmailStage}
              approvalOverview={approvalOverview}
              currentDocumentNumber={proposal.documentNumber}
              currentDocumentVersion={proposal.version}
              documentStatus={proposal.status}
              onRestoreApprovedVersion={handleRestoreApprovedVersion}
              restoreApprovalInProgress={restoreApprovalInProgress}
              onRecoverUnchangedApprovedVersion={handleRecoverUnchangedApprovedVersion}
              recoverUnchangedInProgress={recoverUnchangedInProgress}
              onDownloadHistoricalApprovedPdf={handleDownloadHistoricalApprovedPdf}
              historicalPdfDownloadInProgress={historicalPdfDownloadInProgress}
            />
          </div>
        )}

        <ProposalPreview proposal={proposal} settings={settings} isNewDocument={isNewDocument} removeSpacing={removeSpacing} subtotal={subtotal} taxAmount={taxAmount} total={total} currency={currency} isPublishedView={isPublishedView} leftWidth={leftWidth} previewRef={previewRef} businessProfile={businessProfile} />
      </div>
    </div>
  );
}